/* G-String Custom Bow Builder (sections/gs-bow-builder.liquid): a step-by-step flow under a pinned bow picture.
   - Picture: a stack of see-through layers on one square canvas: the model's base bow for the current view, then
     each picked part color's layer for that view (a color without a picture for a view leaves the plain bow).
     On phones the bow lies on its side in a short strip (gs-builder.css); "View full size" shows it standing up.
   - Price: the model product's version that matches which of the riser and limbs are patterns; if the versions
     don't line up with the parts, the builder won't guess and asks people to call.
   - Each part slot is one native radio group (name "<part>--<slot>"); steps, the Solid/Pattern and finish switches
     and the "All hardware" shortcut only move, filter or set those radios. The switch radios have their own names
     (gsb-finish-*) and never reach the URL or cart.
   - ONE PICK AT A TIME (Joey, Oct 20 2026): inside a color step there are no part tabs. Next walks each color slot
     in the models' part order (Riser, Power limbs, Outboard limbs, Grip, each hardware part, String color 1 and 2,
     Whisker color 1 and 2, Slicks, Cable), Back goes back one pick, and history.state.gsbSub (the slot's code) brings
     the phone's Back button and a reload to the same pick. Review's Edit walks just that row's picks (a string's two
     colors), then goes back to Review. Strings, whiskers and slicks are lists of color names.
   - The page address always holds the build ("angle", not "view": Shopify uses ?view= to switch templates).
   - "Match a past build" (after Specs): the customer sends up to 4 photos of a bow we built (and a description)
     instead of picking colors. The color steps are skipped, the model's all-Solid builder price is charged, and the
     photos upload as files on the cart line ("Match photo 1"…), with the hidden _match flag for the build sheet.
     The photos never go in the page address or a shared link. The resize code is copied from gs-service.js.
   - "Pick from our past builds" (Joey, Oct 20 2026), on the match step: a popup of photos of bows we built (past_build
     entries, loaded from sections/gs-past-builds-json.liquid). A picked photo counts as one of the 4 and goes on the
     cart line as "Match photo N" with the photo's https address (the build sheet shows it as a picture). */
(() => {
  const root = document.querySelector('[data-gsb]');
  if (!root) return;
  const form = root.querySelector('[data-gsb-form]');
  const dataEl = root.querySelector('[data-gsb-models]');
  if (!form || !dataEl) return;
  root.classList.add('is-wizard');
  const models = JSON.parse(dataEl.textContent);
  const phone = root.dataset.phone || '';
  const $ = (sel, el = root) => el.querySelector(sel);
  const $$ = (sel, el = root) => [...el.querySelectorAll(sel)];
  const canvas = $('[data-gsb-canvas]');
  const pic = $('[data-gsb-pic]');
  const note = $('[data-gsb-note]');
  const modelNote = $('[data-gsb-model-note]');
  const msg = $('[data-gsb-msg]');
  const live = $('[data-gsb-live]');
  const shareMsg = $('[data-gsb-share-msg]');
  const nextBtn = $('[data-gsb-next]');
  const backBtn = $('[data-gsb-back]');
  const left = $('[data-gsb-left]');
  const stepList = $('[data-gsb-steplist]');
  const toastEl = $('[data-gsb-toast]');
  const slots = $$('[data-gsb-part]', form);
  const panels = $$('[data-gsb-panel]', form);
  const panes = $$('[data-gsb-step]', form);
  const groups = {};
  $$('[data-gsb-group]', form).forEach(g => { groups[g.dataset.gsbGroup] = g; });
  const GROUP_LIST = { hand: 'hands', draw: 'lengths', weight: 'weights' };
  const VIEW_NAMES = ['left', 'left angle', 'right angle', 'right'];
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const phoneUI = () => window.matchMedia('(max-width: 860px)').matches;
  // a phone whose picture stays pinned under the header (not when it would take too much of the screen, e.g. held sideways)
  const pinnedPhone = () => phoneUI() && !left.classList.contains('is-unpinned');
  const smooth = () => (reduced() ? 'instant' : 'smooth');
  let view = 0;
  let lastPrice = null;
  let priceText = '';
  let current = 'model';
  let adding = false;
  // came from Review's Edit: '' = no; '*' = a step without picks (Model, Specs, Match); otherwise the slot codes of the
  // review row being changed ("string string2"): Next and Back stay on those picks, then go back to Review
  let fromReview = '';
  let openedFromLink = false;
  let toastSnap = null;
  let sub = '';          // the pick shown in the current color step: its slot code (riser, plimbs, string2, whiskers2…)
  let picSaid = '';      // the build in words, for the pictures' screen reader labels ("Riser color Red (Powder coat), …")
  const visited = new Set();
  // This visit's pages in the browser's history: each page the builder adds is numbered (history.state.gsbAt), and
  // navKeys[n] is the stop page n shows ("step|pick"; "…|full" and "…|gal" for the popups). Back uses the browser's own
  // Back when it lands where Back should go, and the phone's Back passes over pages that would show nothing new.
  let navAt = 0;
  const navKeys = [];
  // Match a past build
  const matchPane = $('[data-gsb-match]');
  const matchBody = $('[data-gsb-match-body]');
  const photosBox = $('[data-gsb-photos]');
  const fileIn = $('[data-gsb-file]');
  const thumbs = $('[data-gsb-thumbs]');
  const notesEl = $('[data-gsb-notes]');
  const shot = $('[data-gsb-shot]');
  const photoViews = $('[data-gsb-photoviews]');
  const angleViews = $('[data-gsb-angles]');
  const gal = $('[data-gsb-gal]');      // "Pick from our past builds" popup (only when the store has past-build photos)
  const hasPast = !!gal && !!photosBox && photosBox.hasAttribute('data-gsb-past');
  const MAX_PHOTOS = 4;
  const MAX_SIDE = 2000;                // big phone photos are shrunk to this many pixels on the long side
  const KEEP_BYTES = 1.5 * 1024 * 1024; // a photo already this small (and no bigger than MAX_SIDE) is sent as it is
  const MAX_BYTES = 5 * 1024 * 1024;    // a photo we can't shrink here (e.g. HEIC on some computers) must be under this
  // { file, url ('' = no preview here), name }; a past build picked from the popup: { past (its handle), model, cap, url
  // (the big picture), thumb, link (the https address for the order), name } and no file
  const photos = [];
  let shown = 0;                        // the photo on the plate; -1 = the drawing
  let photoJob = null;                  // photos being made ready
  // why the photos are missing when a match build is opened again: '' | 'reload' | 'link' | 'cart' (the cart's link back to the bow)
  let matchNote = '';
  const matching = () => !!matchPane && matchPane.querySelector('input[name="gsb-match"]:checked')?.value === 'yes';
  // while matching photos, every color step is skipped
  const skipped = id => matching() && !['model', 'specs', 'match', 'review'].includes(id);
  const photoMode = () => matching() && photos.length > 0 && shown >= 0;
  const ownPhotos = () => photos.filter(p => !p.past).length; // the customer's own photos (not picked from our past builds)
  let galPop = false;                   // the history.back() after the popup's Close, Done or Esc is ours, not a Back press

  const slug = s => String(s).toLowerCase().replace(/[^a-z0-9.]+/g, '-').replace(/^-|-$/g, '');
  const money = cents => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: cents % 100 ? 2 : 0 });
  const model = () => models.find(m => m.handle === form.querySelector('input[name="model"]:checked')?.value) || models[0];
  const picked = slot => {
    const i = slot.querySelector('input[name$="--' + slot.dataset.index + '"]:checked');
    return i && i.value ? i : null;
  };
  const groupValue = g => groups[g]?.querySelector('input:checked')?.value || '';
  const activeGroups = () => Object.keys(GROUP_LIST).filter(g => !groups[g].hidden);
  const visible = () => slots.filter(p => !p.hidden);
  const setErr = (el, text) => { const e = el.querySelector('[data-gsb-err]'); if (e) e.textContent = text || ''; el.classList.toggle('is-missing', !!text); };
  // a part's own color slots (not those of parts grouped after it, like whiskers and slicks after the string)
  const slotsOf = panel => $$('[data-gsb-part]', panel).filter(s => s.closest('[data-gsb-panel]') === panel);
  const firstSlot = panel => slotsOf(panel)[0];
  // a slot's part: its panel, or a part grouped inside another part's panel (whiskers, slicks)
  const holderOf = slot => slot.closest('[data-gsb-panel]');
  // a part picked from a list of color names (string, whiskers, slicks: builder_part.as_list)
  const isList = slot => !!slot && !!slot.querySelector('.gsb-swatches--list');
  // Colors that mean the same thing on different parts ("Burnt Orange Anodize" riser = "Burnt Orange" cams)
  const matchKey = inp => {
    if (!inp || !inp.value) return '';
    if (inp.dataset.match) return inp.dataset.match.toLowerCase().replace(/[^a-z0-9]/g, '');
    const words = (inp.dataset.name || '').trim().split(/\s+/);
    const fin = (inp.dataset.finish || '').replace(/\s+/g, '').toLowerCase();
    if (fin && words.length > 1 && words[words.length - 1].toLowerCase() === fin) words.pop();
    return words.join('').toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  /* Finishes: an aluminum part (data-finishes) is built in one finish (Anodize, Powder coat, Cerakote). Its solid colors,
     Black included, come in each finish the color offers (data-fins; none listed = all); a pattern has no finish.
     The finish is part of the build (address, cart, order); the first finish is the standard one. */
  const finishesOf = el => (el && el.dataset.finishes ? el.dataset.finishes.split('|') : []);
  const isAlu = el => finishesOf(el).length > 0;
  const finOf = slot => (isAlu(slot) ? slot.dataset.fin || finishesOf(slot)[0] : '');
  const offeredIn = (inp, f) => !inp.dataset.fins || inp.dataset.fins.split('|').includes(f);
  // RAW = NO FINISH (Joey, Oct 18 2026): a "Raw" or "No paint" color has no Anodize / Powder coat / Cerakote, so its
  // Finish switch hides and no finish goes on the order (the same names as snippets/gs-nofinish.liquid)
  const isRaw = inp => {
    if (!inp || !inp.value || inp.hasAttribute('data-pattern')) return false;
    const h = slug(inp.dataset.short || inp.dataset.name || '');
    return h === 'raw' || h.startsWith('raw-') || h.includes('no-paint');
  };
  const noFin = inp => !!inp && (inp.hasAttribute('data-pattern') || isRaw(inp)); // a pattern or Raw: no finish
  // How a pattern is made, when the order should say it: power-limb patterns are film dipped ("Kryptek · Film dip")
  const patFin = slot => (slot && slot.dataset.patternFinish) || '';
  // What the shop builds for a slot, in words: "Red · Powder coat", "Black · Anodize", "Kryptek", "None"
  function colorText(slot, sep = ' · ') {
    const inp = picked(slot);
    if (!inp && slot.dataset.index !== '1') return 'None';
    if (!inp && slot.hasAttribute('data-required')) return '';
    const name = inp ? (isAlu(slot) && !inp.hasAttribute('data-pattern') ? inp.dataset.short || inp.dataset.name : inp.dataset.name) : 'Black';
    if (inp && inp.hasAttribute('data-pattern')) return patFin(slot) ? name + sep + patFin(slot) : name;
    return isAlu(slot) && !isRaw(inp) ? name + sep + finOf(slot) : name;
  }
  const isStandard = slot => !picked(slot) && !slot.hasAttribute('data-required') && (!isAlu(slot) || finOf(slot) === finishesOf(slot)[0]);

  // Color 1 of a two-color part (string, whiskers) left on Black, while a later color is picked: that's a real
  // Black, as the screen shows ("Color 1 · Black / Color 2 · Blue" is a black and blue string)
  const blackLead = s => s && s.dataset.index === '1' && Number(s.dataset.picks) > 1 && !picked(s) && !s.hasAttribute('data-required')
    && slots.some(x => x !== s && !x.hidden && x.dataset.gsbPart === s.dataset.gsbPart && picked(x));
  const blackInput = s => s.querySelector('input[name$="--' + s.dataset.index + '"][value=""]');

  /* Picks per part, in slot order. Color 1 left on Black counts as Black when Color 2 is picked. */
  function partPicks() {
    const out = [];
    visible().forEach(s => {
      let part = out.find(x => x.handle === s.dataset.gsbPart);
      if (!part) out.push(part = { handle: s.dataset.gsbPart, key: s.dataset.key, name: s.dataset.name, picks: Number(s.dataset.picks) || 1, required: false, chosen: [] });
      if (s.hasAttribute('data-required')) part.required = true;
      const i = picked(s) || (blackLead(s) ? blackInput(s) : null);
      if (i) part.chosen.push(i);
    });
    out.forEach(p => {
      p.rows = p.chosen.map((inp, n) => ({
        label: p.picks > 1 && p.chosen.length > 1 ? p.name + ' ' + (n + 1) : p.name,
        code: n ? p.key + (n + 1) : p.key,
        inp,
      }));
    });
    return out;
  }
  // Parts left on Black (the plain bow in the picture is black too), grouped by finish for the order:
  // { "Black (Anodize)": ["Riser", "Cams"], "Black": ["Molded Grip"] }
  function blackParts() {
    const out = {};
    partPicks().filter(p => !p.rows.length && !p.required).forEach(p => {
      const s = visible().find(x => x.dataset.gsbPart === p.handle && x.dataset.index === '1');
      const k = s && isAlu(s) ? 'Black (' + finOf(s) + ')' : 'Black';
      (out[k] ||= []).push(p.name);
    });
    return out;
  }
  // One picked color for the order: "Red (Powder coat)" on aluminum, "Kryptek", "Kryptek (Film dip)", "Red & Black"
  const rowText = r => {
    const s = r.inp.closest('[data-gsb-part]');
    if (r.inp.hasAttribute('data-pattern')) return r.inp.dataset.name + (patFin(s) ? ' (' + patFin(s) + ')' : '');
    if (isAlu(s) && isRaw(r.inp)) return r.inp.dataset.short || r.inp.dataset.name;
    return isAlu(s) ? (r.inp.dataset.short || r.inp.dataset.name) + ' (' + finOf(s) + ')' : r.inp.dataset.name;
  };

  /* ---------- picture: base + one layer per drawn part, stacked in layer order ---------- */
  const base = document.createElement('img');
  base.className = 'gsb-layer gsb-layer--base';
  base.alt = '';
  pic.append(base);
  let lastLayer = base;
  const layers = slots.filter(p => p.hasAttribute('data-drawn'))
    .sort((a, b) => Number(a.dataset.layer) - Number(b.dataset.layer))
    .map(p => {
      const img = document.createElement('img');
      img.className = 'gsb-layer';
      img.alt = '';
      img.hidden = true;
      lastLayer.after(img);
      lastLayer = img;
      return { part: p, img };
    });
  // The layers are squares: as tall as the canvas on computers (the sides are cropped); on phones the bow lies on its
  // side and the square is sized from the canvas width, much bigger than the short strip. Measure the square itself
  // (its untransformed size), so phones get pictures sharp enough for the long bow.
  const width = () => {
    const px = (pic.offsetHeight || canvas.clientHeight || 600) * (window.devicePixelRatio || 1);
    return px > 900 ? 1200 : px > 600 ? 900 : 600;
  };
  const sized = url => (url ? url.replace(/([?&])width=\d+/, '$1width=' + width()) : '');
  const preload = url => new Promise(res => {
    const i = new Image();
    i.onload = i.onerror = () => res();
    i.src = url;
  });
  let drawToken = 0;
  async function draw() {
    const token = ++drawToken;
    const m = model();
    const plan = [{ img: base, url: sized(m.views[view] || m.views[0]) }];
    const leftOnly = [];
    layers.forEach(({ part, img }) => {
      const inp = !part.hidden && picked(part);
      const urls = inp ? inp.dataset.views.split('|') : [];
      const url = urls[view] || '';
      if (inp && !url && urls[0]) leftOnly.push(inp.dataset.name + ' (' + part.dataset.name.toLowerCase() + ')');
      plan.push({ img, url: sized(url) });
    });
    await Promise.all(plan.filter(p => p.url).map(p => preload(p.url)));
    if (token !== drawToken) return;
    // which way the bow lies on phones (gs-builder.css) changes with the angle, so it changes with the new pictures
    canvas.dataset.view = String(view);
    plan.forEach(({ img, url }) => {
      if (url) { if (img.getAttribute('src') !== url) img.src = url; img.hidden = false; }
      else { img.hidden = true; img.removeAttribute('src'); }
    });
    if (matching()) note.textContent = photos.length ? '' : 'Your photos will show here once you add them.';
    else note.textContent = leftOnly.length ? leftOnly.join(', ') + (leftOnly.length > 1 ? ' are' : ' is') + ' only pictured in the left view so far.' : (m.note || '');
    if (modelNote) modelNote.textContent = m.note || '';
  }
  const layerUrls = slot => {
    const inps = $$('input[name$="--1"]', slot).filter(i => i.value);
    return inps.map(i => (i.dataset.views || '').split('|'));
  };
  const hasLayerForView = (panel, v) => { const s = firstSlot(panel); return !!s && s.hasAttribute('data-drawn') && layerUrls(s).some(u => u[v]); };
  function setView(v, quiet) {
    view = v;
    $$('[data-gsb-view]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.gsbView) === view)));
    if (!quiet) refresh();
  }

  /* ---------- hand, draw length, draw weight: chips from the model ---------- */
  function renderChips(m) {
    const dropped = [];
    Object.entries(GROUP_LIST).forEach(([g, key]) => {
      const box = groups[g].querySelector('.gsb-chips');
      const keep = groupValue(g);
      const unit = groups[g].dataset.unit || '';
      const values = Array.isArray(m[key]) ? m[key] : [];
      groups[g].hidden = values.length === 0;
      box.textContent = '';
      values.forEach(v => {
        const label = document.createElement('label');
        label.className = 'gsb-chip';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = g;
        input.value = v;
        input.checked = v === keep;
        const span = document.createElement('span');
        span.textContent = v + unit;
        label.append(input, span);
        box.append(label);
      });
      if (keep && values.length && !values.includes(keep)) dropped.push([g, 'The ' + m.name + ' doesn\'t come in a ' + keep + unit + ' ' + groups[g].dataset.label.toLowerCase() + '. Please pick again.']);
    });
    // "Need more than 40 lb? The G-Rex goes up to 70 lb." when another model goes heavier
    const up = $('[data-gsb-upsell]');
    if (up) {
      const maxW = mm => Math.max(0, ...(mm.weights || []).map(Number).filter(n => !Number.isNaN(n)));
      const mine = maxW(m);
      const best = models.reduce((a, b) => (maxW(b) > maxW(a) ? b : a), m);
      up.textContent = mine && maxW(best) > mine ? 'Need more than ' + mine + ' lb? The ' + best.name + ' goes up to ' + maxW(best) + ' lb.' : '';
    }
    return dropped;
  }

  /* ---------- price: the product version that matches pattern picks ---------- */
  function priceSetup(m) {
    return m.options.map(o => {
      const part = visible().find(s => s.dataset.priceOption && s.dataset.priceOption.trim().toLowerCase() === o.name.trim().toLowerCase());
      const pat = o.values.filter(v => /pattern/i.test(v));
      const solid = o.values.filter(v => !/pattern/i.test(v));
      return part && pat.length === 1 && solid.length === 1 ? { part, pattern: pat[0], solid: solid[0] } : null;
    });
  }
  const findVariant = (m, want) => m.variants.find(v => v.options.length === want.length && v.options.every((x, i) => x === want[i])) || null;
  function variant(m) {
    const setup = priceSetup(m);
    if (setup.some(x => !x)) return null;
    return findVariant(m, setup.map(x => (picked(x.part)?.hasAttribute('data-pattern') ? x.pattern : x.solid)));
  }
  // The model's all-Solid builder version: what a "Match a past build" order pays now (Joey invoices any difference)
  function baseVariant(m) {
    const setup = priceSetup(m);
    const v = setup.some(x => !x) ? null : findVariant(m, setup.map(x => x.solid));
    if (v) return v;
    const solid = m.variants.filter(x => x.options.every(o => !/pattern/i.test(o)));
    return solid.length === 1 ? solid[0] : null;
  }
  const priceVariant = m => (matching() ? baseVariant(m) : variant(m));
  // What a pattern on these slots adds to the all-solid price (0 when it can't be priced)
  function patternCost(slotEls) {
    const m = model();
    const setup = priceSetup(m);
    if (setup.some(x => !x)) return 0;
    const base0 = findVariant(m, setup.map(x => x.solid));
    const withPat = findVariant(m, setup.map(x => (slotEls.includes(x.part) ? x.pattern : x.solid)));
    return base0 && withPat ? withPat.price - base0.price : 0;
  }

  /* ---------- sets: "All hardware" (builder_part.match_group) ----------
     One color for a whole group of parts, offered on the group's first pick as "Use this color for all the hardware"
     (Joey, Oct 20 2026; it was a tab before). The later picks are then already set, and Next still walks them, so each
     can be changed. Only a group of SET_MIN parts or more gets the shortcut: for two parts (the "Both limbs" group) one
     more Next is as quick, and Joey wants the power limbs and the outboard limbs picked one at a time. */
  const SET_MIN = 3;
  const sets = [];
  panes.forEach(pane => {
    const byGroup = {};
    $$('[data-gsb-panel][data-match-group]', pane).forEach(p => { (byGroup[p.dataset.matchGroup] ||= []).push(p); });
    Object.entries(byGroup).forEach(([name, members]) => {
      if (members.length < SET_MIN) return;
      const id = 'set-' + slug(pane.dataset.gsbStep + '-' + name);
      const box = document.createElement('div');
      box.className = 'gsb-allset';
      box.id = 'gsb-' + id;
      box.innerHTML = '<div class="gsb-allset__row"><button type="button" class="gsb-allset__btn" data-gsb-allset></button>'
        + '<button type="button" class="gsb-match" data-gsb-matchriser hidden></button></div><p class="gsb-allset__s" data-gsb-allset-s></p>';
      sets.push({ id, name, pane, members, box });
    });
  });
  const visibleMembers = set => set.members.filter(p => !p.hidden);
  // "All hardware" -> "all the hardware" (the button reads "Use this color for all the hardware")
  const setWords = set => set.name.toLowerCase().replace(/^all (?!the )/, 'all the ');
  // every part in the set is aluminum with the same finishes (e.g. all hardware): one finish can be named for all
  const sameFinishes = mem => { const f = mem.map(p => firstSlot(p).dataset.finishes || ''); return !!f[0] && f.every(x => x === f[0]); };
  // The shortcut sits on the first part's pick (the Gnat has no wheels or cams, so it's the pockets on every model),
  // right under the color it would use ("Black · Anodize"): under the 17 colors it fell below the screen on small phones
  function placeSets() {
    sets.forEach(set => {
      const mem = visibleMembers(set);
      set.box.hidden = mem.length < SET_MIN;
      if (mem.length < SET_MIN) return;
      const readout = firstSlot(mem[0]).querySelector('.gsb-readout');
      if (set.box.previousElementSibling !== readout) readout.after(set.box);
    });
  }
  // Set every part in a set to one color (by match key; '' = Black) and, for a solid on aluminum parts, one finish
  function applySet(set, key, fin) {
    const snap = snapshot();
    const done = [];
    const skipped = [];
    let name = 'Black';
    let pattern = false;
    let raw = false;
    visibleMembers(set).forEach(p => {
      const s = firstSlot(p);
      const useFin = fin && finishesOf(s).includes(fin) ? fin : finOf(s);
      const target = $$('input[name$="--1"]', s).find(i => matchKey(i) === key && (key || !i.value) && (!isAlu(s) || i.hasAttribute('data-pattern') || offeredIn(i, useFin)));
      if (!target) { skipped.push(p.dataset.short); return; }
      target.checked = true;
      if (fin && isAlu(s) && !target.hasAttribute('data-pattern') && finishesOf(s).includes(fin)) s.dataset.fin = fin;
      done.push(p.dataset.short);
      if (key) name = target.dataset.short || target.dataset.name;
      pattern = target.hasAttribute('data-pattern');
      raw = isRaw(target);
    });
    refresh();
    syncAllFinish();
    allTiles();
    const mem = visibleMembers(set);
    const cost = patternCost(mem.map(firstSlot).filter(s => picked(s)?.hasAttribute('data-pattern')));
    const aluNames = mem.filter(p => isAlu(firstSlot(p)) && finishesOf(firstSlot(p)).includes(fin)).map(p => p.dataset.short.toLowerCase());
    const finWords = !fin || pattern || raw ? '' : sameFinishes(mem) ? ' · ' + fin : aluNames.length ? ' (' + aluNames.join(', ') + ' ' + fin + ')' : '';
    showToast((done.length === set.members.length || done.length > 1 ? set.name : done.join(', ')) + ' set to ' + name + finWords + (cost ? ' (+' + money(cost) + ')' : '') + '.' + (skipped.length ? ' ' + skipped.join(', ') + " doesn't come in that color." : ''), snap);
  }
  // A finish tapped on a part: a solid pick takes that finish; a color not offered in it goes back to Black
  function applyFinish(s, fin) {
    const snap = snapshot();
    s.dataset.fin = fin;
    const p = picked(s);
    let reset = '';
    if (p && !p.hasAttribute('data-pattern') && !offeredIn(p, fin)) {
      const own = $$('input[name$="--' + s.dataset.index + '"]', s);
      const black = own.find(i => !i.value) || own.find(i => matchKey(i) === 'black' && !i.hasAttribute('data-pattern') && offeredIn(i, fin));
      if (black) { black.checked = true; reset = s.dataset.label + ' (' + (p.dataset.short || p.dataset.name) + ')'; }
    }
    refresh();
    if (reset) showToast(reset + " isn't offered in " + fin + ' yet, so it went back to Black.', snap);
  }
  // Each set: is it all one color (for the review and the step summaries), and its shortcut's words
  function syncSets() {
    sets.forEach(set => {
      const mem = visibleMembers(set);
      if (mem.length < SET_MIN) return;
      const keys = mem.map(p => matchKey(picked(firstSlot(p))));
      // parts in the set that have a finish (on a solid color), each compared with its own standard finish
      const alu = mem.filter(p => isAlu(firstSlot(p)) && !noFin(picked(firstSlot(p))));
      const fins = alu.map(p => finOf(firstSlot(p)));
      const finSame = fins.every(f => f === fins[0]);
      const allStd = alu.every(p => finOf(firstSlot(p)) === finishesOf(firstSlot(p))[0]);
      const common = sameFinishes(mem);
      const same = keys.every(k => k === keys[0]) && (!common || finSame);
      const ref = picked(firstSlot(mem[0]));
      let finText = '';
      if (fins.length) {
        if (common) finText = ' · ' + fins[0];
        else if (!allStd) finText = ' · ' + alu.filter(p => finOf(firstSlot(p)) !== finishesOf(firstSlot(p))[0]).map(p => p.dataset.short.toLowerCase() + ' ' + finOf(firstSlot(p))).join(', ');
      }
      const label = same ? (keys[0] ? (ref ? ref.dataset.short || ref.dataset.name : 'Mixed') : 'Black') + finText : 'Mixed';
      set.mixed = !same;
      set.label = label;
      set.standard = same && !keys[0] && allStd;
      // the shortcut: "Use this color for all the hardware" (nothing to do while they all match)
      const btn = $('[data-gsb-allset]', set.box);
      btn.textContent = 'Use this color for ' + setWords(set);
      // nothing to do while they all match; aria-disabled (not disabled) keeps the keyboard on the button after it's used
      btn.setAttribute('aria-disabled', String(same));
      const names = mem.map(p => p.dataset.name.toLowerCase());
      const list = names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1] : names[0];
      $('[data-gsb-allset-s]', set.box).textContent = same
        ? 'All ' + mem.length + ' parts are ' + label + '.'
        : 'Sets the ' + list + ' to ' + colorText(firstSlot(mem[0])) + '. You can still change each one.';
      // Match riser: only when every part in the set comes in the riser's color
      const mr = $('[data-gsb-matchriser]', set.box);
      const riser = slots.find(s => s.dataset.key === 'riser' && !s.hidden);
      const rk = riser ? matchKey(picked(riser)) : '';
      const can = rk && mem.every(p => $$('input[name$="--1"]', firstSlot(p)).some(i => matchKey(i) === rk));
      mr.hidden = !can;
      if (can) {
        // the riser's finish comes along for every part that can take one, whatever those parts have picked now
        const rf = mem.some(p => isAlu(firstSlot(p))) && isAlu(riser) && !noFin(picked(riser)) ? finOf(riser) : '';
        mr.textContent = 'Match riser';
        mr.setAttribute('aria-label', 'Match riser: ' + colorText(riser, ', ') + ', for ' + setWords(set));
        mr.dataset.key = rk;
        mr.dataset.fin = rf;
      }
    });
  }

  /* ---------- finish switch (Solid / Pattern, or an aluminum part's finishes), names, "All N" ---------- */
  const isPatternLabel = l => l.classList.contains('gsb-sw--pattern');
  // is this swatch shown under that tab of the switch?
  const inTab = (label, g, alu) => (alu
    ? (g === 'Pattern' ? isPatternLabel(label) : !isPatternLabel(label) && offeredIn(label.querySelector('input'), g))
    : (label.dataset.group || 'Solid') === g);
  function switchOrder(holder, labels) {
    if (isAlu(holder)) {
      const order = finishesOf(holder).filter(f => labels.some(l => inTab(l, f, true)));
      if (labels.some(isPatternLabel)) order.push('Pattern');
      return order;
    }
    const order = [];
    labels.forEach(l => { const g = l.dataset.group || 'Solid'; if (!order.includes(g)) order.push(g); });
    return order.sort((a, b) => (a === 'Solid' ? -1 : b === 'Solid' ? 1 : 0));
  }
  function finishSwitch(holder, swatches) {
    const labels = $$('label.gsb-sw', swatches);
    const alu = isAlu(holder);
    const order = switchOrder(holder, labels);
    let sw = holder.querySelector(':scope > .gsb-finish');
    // a list of color names (strings, whiskers, slicks) is one list, two-tones included: no Solid | Two-tone switch
    // (Joey, Oct 20 2026). Aluminum parts keep their Anodize | Powder coat | Cerakote | Pattern switch.
    const oneList = !alu && swatches.classList.contains('gsb-swatches--list');
    if (order.length < 2 || oneList) { if (sw) sw.remove(); return; }
    if (!sw) {
      sw = document.createElement('fieldset');
      sw.className = 'gsb-finish';
      swatches.before(sw);
    }
    sw.classList.toggle('gsb-finish--many', order.length > 3);
    const name = 'gsb-finish-' + (holder.closest('[data-gsb-panel]')?.id || '') + '-' + holder.dataset.slot;
    const keep = sw.querySelector('input:checked')?.value;
    sw.innerHTML = '<legend class="gsm-sr">' + (alu ? 'Finish' : 'Show') + '</legend>';
    order.forEach(g => {
      const n = labels.filter(l => inTab(l, g, alu) && !(l.classList.contains('gsb-sw--none') && !l.classList.contains('gsb-sw--black'))).length;
      const lab = document.createElement('label');
      lab.className = 'gsb-finish__opt';
      const inp = document.createElement('input');
      inp.type = 'radio';
      inp.name = name;
      inp.value = g;
      inp.checked = g === (order.includes(keep) ? keep : order[0]);
      const t = document.createElement('span');
      t.className = 'gsb-finish__t';
      t.textContent = g + ' ' + n;
      const c = document.createElement('span');
      c.className = 'gsb-finish__c';
      c.dataset.gsbFinishCost = g;
      lab.append(inp, t, c);
      sw.append(lab);
    });
  }
  function finishCosts() {
    $$('.gsb-finish').forEach(sw => {
      const holder = sw.parentElement;
      const slotEls = holder.matches('[data-gsb-part]') ? [holder] : [];
      $$('[data-gsb-finish-cost]', sw).forEach(c => {
        const cost = /pattern/i.test(c.dataset.gsbFinishCost) ? patternCost(slotEls.filter(s => s.dataset.priceOption)) : 0;
        c.textContent = cost ? ' · +' + money(cost) : '';
        const inp = c.parentElement.querySelector('input');
        inp.setAttribute('aria-label', c.dataset.gsbFinishCost + ', ' + c.previousElementSibling.textContent.replace(/\D+/g, '') + ' colors' + (cost ? ', adds ' + money(cost) : ''));
      });
    });
  }
  function filterFinish(holder, group) {
    const alu = isAlu(holder);
    $$('label.gsb-sw', $('[data-gsb-swatches]', holder)).forEach(l => l.classList.toggle('is-filtered', !inTab(l, group, alu)));
  }
  // Open a tray on its current pick: its group (Black counts as Solid), or on aluminum its finish (Pattern for a pattern)
  function syncFinish(holder) {
    const sw = holder.querySelector(':scope > .gsb-finish');
    if (!sw) return;
    const cur = holder.querySelector('[data-gsb-swatches] input:checked');
    let g;
    if (isAlu(holder)) g = cur && cur.hasAttribute('data-pattern') ? 'Pattern' : holder.dataset.fin || finishesOf(holder)[0];
    else g = cur ? cur.closest('label').dataset.group || 'Solid' : 'Solid';
    const inp = $$('input', sw).find(i => i.value === g) || sw.querySelector('input');
    inp.checked = true;
    filterFinish(holder, inp.value);
  }
  const syncAllFinish = () => slots.forEach(syncFinish);
  // RAW = NO FINISH: while a part is on a Raw color, its Finish switch is hidden
  function rawSwitches() {
    slots.forEach(s => { const sw = s.querySelector(':scope > .gsb-finish'); if (sw && isAlu(s)) sw.hidden = isRaw(picked(s)); });
  }
  const NAMES_KEY = 'gsb-names';
  const namesBtn = $('[data-gsb-names]');
  function setNames(on) {
    root.classList.toggle('is-names', on);
    namesBtn.setAttribute('aria-pressed', String(on));
    namesBtn.textContent = on ? 'Hide names' : 'Show names';
    try { localStorage.setItem(NAMES_KEY, on ? '1' : ''); } catch (e) { /* private window: just don't remember */ }
  }
  namesBtn.addEventListener('click', () => setNames(!root.classList.contains('is-names')));
  try { if (localStorage.getItem(NAMES_KEY)) setNames(true); } catch (e) { /* ignore */ }
  function allTiles() {
    $$('[data-gsb-swatches]').forEach(box => {
      box.querySelector('.gsb-all')?.remove();
      if (box.classList.contains('gsb-swatches--list')) return; // named lists never turn into side rails
      const n = $$('label.gsb-sw:not(.is-filtered):not(.gsb-sw--none)', box).length;
      const pattern = !!box.querySelector('label.gsb-sw--pattern:not(.is-filtered)');
      box.classList.toggle('is-rail', n > 24 || pattern);
      box.classList.toggle('is-coupons', pattern);
      if (n <= 24 && !pattern) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gsb-all';
      b.textContent = 'All ' + n + ' ›';
      b.setAttribute('aria-label', 'Show all ' + n + ' colors with names');
      b.addEventListener('click', () => {
        setNames(true);
        box.scrollIntoView({ behavior: smooth(), block: 'start' });
        (box.querySelector('input:checked') || box.querySelector('label:not(.is-filtered) input'))?.focus({ preventScroll: true });
      });
      box.append(b);
    });
  }

  /* ---------- one pick at a time (Joey, Oct 20 2026) ----------
     A color step shows one color slot (a "pick") at a time; Next and Back walk them in the models' part order
     (builder_model.parts, set by data/load_builder.py PART_ORDER). A pick is known by its slot's code (riser, plimbs,
     string2, whiskers2…), kept in history.state.gsbSub (never in the address). */
  const isColorStep = id => !['model', 'specs', 'match', 'review'].includes(id);
  // a color step's picks, in order (parts the model doesn't have, like the Gnat's cams, drop out)
  const itemsOf = pane => (pane ? slots.filter(s => !s.hidden && pane.contains(s)) : []);
  const slotByCode = code => (code ? slots.find(s => s.dataset.slot === code && !s.hidden) : null);
  const curSlot = () => (isColorStep(current) ? slotByCode(sub) : null);
  // What's being picked, in plain words: "Power Limbs", "String color 1", "Whisker color 2", "Slick color". Screen only:
  // the cart and the order keep their own names ("String 1 color", "Whiskers 2 color")
  function itemTitle(slot) {
    const n = Number(slot.dataset.picks) > 1 ? ' ' + slot.dataset.index : '';
    if (!n && !isList(slot)) return slot.dataset.name;
    return slot.dataset.name.replace(/([^s])s$/i, '$1') + ' color' + n;
  }
  // Show one pick: its part's panel (or grouped part, like whiskers), its own slot, nothing else in the step
  function showItem(pane, slot) {
    const holder = holderOf(slot);
    const top = holder.closest('.gsb-panel');
    $$('.gsb-panel', pane).forEach(p => { const on = p === top; p.classList.toggle('is-off', !on); p.inert = !on; });
    const onOff = (el, on) => { el.classList.toggle('is-off', !on); el.inert = !on; };
    slotsOf(top).forEach(s => onOff(s, s === slot));
    $$('[data-gsb-sub]', top).forEach(x => { onOff(x, x === holder); slotsOf(x).forEach(s => onOff(s, s === slot)); });
    // "I want custom string and cable lengths" comes with the step's last pick (the Cable color)
    const its = itemsOf(pane);
    if (customBox && pane.contains(customBox)) onOff(customBox, slot === its[its.length - 1]);
    syncFinish(slot);
    allTiles();
    const v = holder.dataset.view;
    if (v != null && v !== '' && hasLayerForView(holder, Number(v)) && view !== Number(v)) setView(Number(v));
  }
  // every stop Next and Back go through: the steps, with each color step's picks one by one
  function walk() {
    const out = [];
    navIds().forEach(id => {
      const its = isColorStep(id) ? itemsOf(paneOf(id)) : [];
      if (its.length) its.forEach(s => out.push({ id, sub: s.dataset.slot }));
      else out.push({ id, sub: '' });
    });
    return out;
  }
  const stopKey = (id, code) => id + '|' + (code || '');
  const here = () => { const w = walk(); return { w, i: w.findIndex(p => p.id === current && p.sub === sub) }; };
  const stopTitle = p => (p.sub ? itemTitle(slotByCode(p.sub)) : paneOf(p.id).dataset.title);
  // Where Next and Back go from here. After Review's Edit they stay on that row's picks (a string's color 1 and
  // color 2; every part of "All hardware") and otherwise go straight back to Review, the way the phone's Back does.
  const editing = () => (fromReview && fromReview !== '*' ? fromReview.split(' ') : []);
  const REVIEW = { id: 'review', sub: '' };
  function nextStop() {
    const { w, i } = here();
    const nxt = i >= 0 ? w[i + 1] || null : null;
    if (!fromReview) return nxt;
    return nxt && nxt.id === current && editing().includes(nxt.sub) ? nxt : REVIEW;
  }
  function backStop() {
    const { w, i } = here();
    const prev = i > 0 ? w[i - 1] : null;
    if (!fromReview) return prev;
    return prev && prev.id === current && editing().includes(prev.sub) ? prev : REVIEW;
  }

  /* ---------- readouts ---------- */
  function renderReadouts() {
    slots.forEach(s => {
      const c = s.querySelector('[data-gsb-cost]');
      if (!c) return;
      const p = picked(s);
      if (!s.dataset.priceOption) { c.textContent = ''; return; }
      const cost = p && p.hasAttribute('data-pattern') ? patternCost([s]) : 0;
      c.textContent = cost ? '+' + money(cost) : p || s.dataset.index !== '1' || !s.hasAttribute('data-required') ? 'Included' : '';
    });
    finishCosts();
  }

  /* ---------- "I want custom string and cable lengths" (String & cable step): the boxes for this model only ---------- */
  const customBox = $('[data-gsb-custom]');
  const customOn = () => !!customBox?.querySelector('[data-gs-custom-on]').checked;
  function syncCustom() {
    if (!customBox) return;
    const h = model().handle;
    $$('[data-gsb-custom-for]', customBox).forEach(l => {
      const mine = l.dataset.gsbCustomFor.split(' ').includes(h);
      l.hidden = !mine;
      l.querySelector('input').disabled = !mine || !customOn();
    });
    window.GSCustomCheck?.(customBox); // "at least one length" counts only this model's boxes
  }
  const customErr = text => { const e = customBox?.querySelector('[data-gsb-custom-err]'); if (e) e.textContent = text; };
  // runs after gstring.js turns the boxes on, so the other model's boxes stay off
  document.addEventListener('change', e => { if (e.target.closest('[data-gsb-custom] [data-gs-custom-on]')) { syncCustom(); summary(); saveUrl(); } });
  customBox?.addEventListener('input', () => { customErr(''); summary(); }); // the review's build sheet lists the custom lengths
  customBox?.addEventListener('change', () => { customErr(''); saveUrl(); }); // the address keeps the lengths (a reload)
  const customLengths = () => (customOn() ? $$('[data-gsb-len]', customBox).filter(i => !i.disabled && i.value.trim()).map(i => [i.dataset.gsbLen, i.value.trim()]) : []);

  /* ---------- summary, price, labels ---------- */
  function summary() {
    const m = model();
    const v = priceVariant(m);
    const match = matching();
    const cheapest = Math.min(...m.variants.map(x => x.price));
    const rows = [['Model', m.name]];
    activeGroups().forEach(g => {
      const val = groupValue(g);
      rows.push([groups[g].dataset.label, val ? val + (groups[g].dataset.unit || '') : '']);
      const gv = groups[g].querySelector('[data-gsb-groupval]');
      if (gv) gv.textContent = val ? val + (groups[g].dataset.unit || '') : 'Pick one';
    });
    visible().forEach(s => {
      const inp = picked(s);
      s.querySelector('[data-gsb-pick]').textContent = colorText(s) || 'Choose a color';
      s.classList.toggle('is-set', !!inp);
      const cn = s.querySelector('[data-gsb-color-note]');
      if (cn) cn.textContent = inp && inp.dataset.note ? inp.dataset.name + ': ' + inp.dataset.note : '';
    });
    if (match) {
      // matching photos: no color picks, the photos and notes say it all
      rows.push(['Colors', 'Match my photos'], ['Match photos', photos.length ? photos.length + (photos.length > 1 ? ' photos' : ' photo') : '']);
      if (notesEl.value.trim()) rows.push(['Your notes', notesEl.value.trim()]);
    } else {
      partPicks().forEach(p => {
        if (p.rows.length) p.rows.forEach(r => rows.push([r.label, rowText(r)]));
        else if (p.required) rows.push([p.name, '']);
      });
      Object.entries(blackParts()).forEach(([k, names]) => rows.push([k, names.join(', ')]));
      const cl = customLengths();
      if (cl.length) rows.push(['Custom lengths', cl.map(([k, v]) => k + ' ' + v).join(', ')]);
    }
    const dl = $('[data-gsb-summary]');
    dl.textContent = '';
    rows.forEach(([k, val]) => {
      const dt = document.createElement('dt');
      dt.textContent = k;
      const dd = document.createElement('dd');
      dd.textContent = val || 'Not picked yet';
      if (!val) dd.className = 'is-missing';
      dl.append(dt, dd);
    });
    const bd = $('[data-gsb-breakdown]');
    if (v && match) {
      priceText = money(v.price);
      bd.textContent = m.name + ' builder price. If matching your photos costs more, we\'ll contact you and send an invoice for the difference before we build.';
    } else if (v) {
      priceText = money(v.price);
      const extras = [];
      m.options.forEach((o, i) => { if (/pattern/i.test(v.options[i])) extras.push(o.name.toLowerCase()); });
      bd.textContent = m.name + ' ' + money(cheapest) + (v.price > cheapest ? ' + pattern ' + extras.join(', ') + ' ' + money(v.price - cheapest) : '');
    } else {
      priceText = 'Call us';
      bd.textContent = 'We can\'t price this build online right now. Call or text us at ' + phone + ' and we\'ll get it ordered.';
      console.warn('[bow builder] The product versions for ' + m.name + ' don\'t match the parts\' price options.');
    }
    root.querySelectorAll('[data-gsb-price], [data-gsb-price-top], [data-gsb-price-bar]').forEach(el => { el.textContent = priceText; });
    if (lastPrice !== null && lastPrice !== priceText) live.textContent = 'Total ' + priceText;
    lastPrice = priceText;
    const bm = $('[data-gsb-bar-model]');
    if (bm) bm.textContent = m.name;
    stageTitle();
    if (matchPane) {
      // "Built to your specs: Right hand · 28″ draw · 50 lb" and "You'll pay the builder price now: $1,000"
      const specs = activeGroups().map(g => (groupValue(g) ? groupValue(g) + (groups[g].dataset.unit || '') + (g === 'draw' ? ' draw' : '') : ''));
      $('[data-gsb-match-specs]').textContent = specs.some(Boolean) ? specs.map(x => x || '…').join(' · ') : 'Not picked yet';
      $('[data-gsb-match-price]').textContent = v ? ': ' + priceText : '';
    }
    picSaid = rows.filter(r => r[1] && r[0] !== 'Model').map(r => r[0] + ' ' + r[1]).join(', ');
    pic.setAttribute('aria-label', 'Picture of your ' + m.name + ', ' + VIEW_NAMES[view] + ' view' + (picSaid ? ': ' + picSaid : ''));
    const pb = $('[data-gsb-price-top]');
    if (pb) {
      const ph = phoneUI();
      pb.setAttribute('aria-label', priceText + (ph ? '. Show all steps' : ''));
      pb.tabIndex = ph ? 0 : -1; // on desktop the stepper is always there, so the price is just a label
      if (ph) pb.setAttribute('aria-expanded', String(!stepList.hidden)); else pb.removeAttribute('aria-expanded');
    }
  }

  /* ---------- steps ---------- */
  const paneOf = id => panes.find(p => p.dataset.gsbStep === id);
  function activeSteps() {
    return panes.filter(p => {
      const id = p.dataset.gsbStep;
      if (id === 'model' || id === 'review' || id === 'match') return true;
      if (id === 'specs') return activeGroups().length > 0;
      return $$('[data-gsb-panel]', p).some(x => !x.hidden);
    });
  }
  // the steps Next and Back walk through (the color steps drop out while matching photos)
  const navIds = () => activeSteps().map(p => p.dataset.gsbStep).filter(id => !skipped(id));
  function missingIn(id) {
    const out = [];
    if (skipped(id)) return out;
    if (id === 'specs' || id === 'review') activeGroups().forEach(g => { if (!groupValue(g)) out.push({ el: groups[g], name: groups[g].dataset.label, text: 'Please pick your ' + groups[g].dataset.label.toLowerCase() + '.' }); });
    if ((id === 'match' || id === 'review') && matching() && !photos.length) out.push({ el: photosBox, name: 'Photos of the bow to match', text: (hasPast ? 'Pick a bow from our past builds or add a photo' : 'Add at least one photo') + ', or pick “No, I’ll pick my colors”.' });
    const scope = id === 'review' ? form : paneOf(id);
    if (id !== 'specs' && id !== 'model' && id !== 'match' && scope && !(id === 'review' && matching())) {
      visible().filter(s => scope.contains(s)).forEach(s => { if (s.hasAttribute('data-required') && !picked(s)) out.push({ el: s, name: s.dataset.label + ' color', text: 'Please pick a ' + s.dataset.label.toLowerCase() + ' color.' }); });
    }
    return out;
  }
  function statusOf(id) {
    if (id === current) return 'current';
    if (skipped(id)) return 'skip';
    if (!visited.has(id)) return 'todo';
    return id !== 'review' && missingIn(id).length ? 'attention' : 'done';
  }
  function stepSummary(id) {
    const m = model();
    if (id === 'model') return m.name;
    if (id === 'specs') {
      const vals = activeGroups().map(g => groupValue(g) ? groupValue(g) + (groups[g].dataset.unit || '') : '');
      return vals.some(Boolean) ? vals.map(v => v || '…').join(' · ').replace(/ hand/i, '') : 'Not picked yet';
    }
    if (id === 'review') return 'Total ' + priceText;
    if (id === 'match') return !matching() ? 'No photo' : photos.length ? photos.length + (photos.length > 1 ? ' photos' : ' photo') : 'No photos yet';
    if (skipped(id)) return 'Skipped';
    const pane = paneOf(id);
    const bits = [];
    let standard = 0;
    const inSet = new Set();
    sets.filter(s => s.pane === pane && visibleMembers(s).length >= SET_MIN).forEach(s => {
      const mem = visibleMembers(s);
      if (!s.mixed) {
        mem.forEach(p => inSet.add(p));
        if (s.standard) standard += 1; else bits.push(s.name + ' ' + s.label);
        return;
      }
      // mixed: name the odd ones out, then "rest <most common color>"
      const keyOf = p => { const fs1 = firstSlot(p); const i = picked(fs1); return matchKey(i) + (isAlu(fs1) && !noFin(i) ? '|' + finOf(fs1) : ''); };
      const count = {};
      mem.forEach(p => { count[keyOf(p)] = (count[keyOf(p)] || 0) + 1; });
      const top = Object.keys(count).sort((x, y) => count[y] - count[x])[0];
      if (count[top] < 2) return;
      mem.forEach(p => inSet.add(p));
      mem.filter(p => keyOf(p) !== top).forEach(p => { bits.push(colorText(firstSlot(p), ' ') + ' ' + p.dataset.short.toLowerCase()); });
      const ref = mem.find(p => keyOf(p) === top);
      bits.push('rest of ' + s.name.toLowerCase() + ' ' + colorText(firstSlot(ref), ' '));
    });
    $$('[data-gsb-panel]', pane).filter(p => !p.hidden && !inSet.has(p)).forEach(p => {
      const ss = slotsOf(p).filter(s => !s.hidden);
      const chosen = ss.filter(s => picked(s) || blackLead(s));
      const req = ss.some(s => s.hasAttribute('data-required'));
      if (chosen.length) bits.push(chosen.map(s => colorText(s, ' ')).join(' & ') + ' ' + p.dataset.short.toLowerCase());
      else if (req) bits.push(p.dataset.short + ': not picked');
      else if (ss[0] && !isStandard(ss[0])) bits.push(colorText(ss[0], ' ') + ' ' + p.dataset.short.toLowerCase());
      else standard += 1;
    });
    if (standard) bits.push(bits.length ? 'rest black' : 'All black');
    return bits.join(' · ');
  }
  function renderNav() {
    const list = activeSteps();
    const ids = list.map(p => p.dataset.gsbStep);
    const i = Math.max(0, ids.indexOf(current));
    const title = id => paneOf(id).dataset.title;
    // the pick shown now: "Power Limbs", 2 of 4 in Riser & limbs
    const cs = curSlot();
    const its = cs ? itemsOf(paneOf(current)) : [];
    const k = its.indexOf(cs);
    const count = cs && its.length > 1 ? (k + 1) + ' of ' + its.length : '';
    // in brackets, so "String color 1 (1 of 6)" doesn't read as two numbers in a row
    const countText = count ? '(' + count + ')' : '';
    // phone step header: "Step 4 of 7 · Riser & limbs" over "Power Limbs (2 of 4)"
    $('[data-gsb-stepnum]').textContent = 'Step ' + (i + 1) + ' of ' + ids.length + (cs ? ' · ' + title(current) : '');
    $('[data-gsb-stepname]').textContent = cs ? itemTitle(cs) : title(current);
    $('[data-gsb-stepcount]').textContent = countText;
    $$('.gsb-stephead__btn').forEach(b => b.setAttribute('aria-label', 'Step ' + (i + 1) + ' of ' + ids.length + ', ' + title(current) + (cs ? ': ' + itemTitle(cs) + (count ? ', ' + count : '') : '') + '. All steps'));
    // the step's own heading line (computers): "Power Limbs (2 of 4)"
    const head = $('[data-gsb-itemhead]', paneOf(current));
    if (head && cs) { $('[data-gsb-itemhead-t]', head).textContent = itemTitle(cs); $('[data-gsb-itemhead-n]', head).textContent = countText; }
    // "Show names" only does something on round color swatches (not on the lists of names)
    root.classList.toggle('is-swatches', !!cs && !isList(cs));
    const prog = $('[data-gsb-progress]');
    prog.textContent = '';
    ids.forEach(id => {
      const seg = document.createElement('span');
      seg.className = 'gsb-progress__seg is-' + statusOf(id);
      // the current color step's bar fills as its picks go by
      if (id === current && cs) seg.style.setProperty('--gsb-fill', Math.round(((k + 1) / its.length) * 100) + '%');
      prog.append(seg);
    });
    const word = { done: 'done', attention: 'needs attention', current: 'current step', todo: '', skip: 'skipped' };
    const mark = { done: '✓', attention: '!', current: '●', todo: '○', skip: '–' };
    const build = (ol, phoneList) => {
      ol.textContent = '';
      ids.forEach((id, n) => {
        const st = statusOf(id);
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gsb-stepbtn is-' + st;
        b.dataset.gsbGoto = id;
        if (id === current) b.setAttribute('aria-current', 'step');
        b.innerHTML = '<span class="gsb-stepbtn__n" aria-hidden="true"></span><span class="gsb-stepbtn__t"></span><span class="gsb-stepbtn__s"></span><span class="gsm-sr"></span>';
        b.querySelector('.gsb-stepbtn__n').textContent = phoneList ? mark[st] : st === 'done' ? '✓' : st === 'attention' ? '!' : st === 'skip' ? '–' : String(n + 1);
        b.querySelector('.gsb-stepbtn__t').textContent = title(id);
        b.querySelector('.gsb-stepbtn__s').textContent = stepSummary(id);
        b.title = stepSummary(id); // the whole summary on hover, when the box is too narrow for it
        b.querySelector('.gsm-sr').textContent = word[st] ? ', ' + word[st] : '';
        li.append(b);
        ol.append(li);
      });
    };
    build($('[data-gsb-stepper]'), false);
    build($('[data-gsb-steplist-items]'), true);
    // Back and Next go one pick at a time ("Next: Outboard Limbs", "‹ Back" to Power Limbs)
    const prev = backStop();
    backBtn.hidden = !prev;
    if (prev) { backBtn.textContent = '‹ Back'; backBtn.setAttribute('aria-label', 'Back to ' + stopTitle(prev)); }
    if (current === 'review') {
      nextBtn.type = 'submit';
      nextBtn.textContent = adding ? (matching() && photos.length ? 'Sending photos…' : 'Adding…') : priceText === 'Call us' ? 'Call us to order' : 'Add to cart · ' + priceText;
      nextBtn.disabled = adding || priceText === 'Call us';
    } else {
      nextBtn.type = 'button';
      nextBtn.disabled = false;
      const nxt = nextStop();
      nextBtn.textContent = !nxt || nxt.id === 'review' ? (fromReview ? 'Done: back to review' : 'Review build') : 'Next: ' + stopTitle(nxt);
    }
  }
  // Review: one row per part (both colors of a two-color part on one row: "Red + Black"); Edit opens that part's pick.
  // A set whose parts all match is one row ("All hardware · Black · Anodize").
  function partRows(pane) {
    const out = [];
    const whole = new Map(); // first part of a matching set -> the set
    const inSet = new Set();
    sets.filter(s => s.pane === pane && visibleMembers(s).length >= SET_MIN && !s.mixed).forEach(s => {
      const mem = visibleMembers(s);
      mem.forEach(p => inSet.add(p));
      whole.set(mem[0], s);
    });
    const codes = list => list.map(s => s.dataset.slot).join(' ');
    [...new Set(itemsOf(pane).map(holderOf))].forEach(h => {
      const ss = slotsOf(h).filter(s => !s.hidden);
      if (whole.has(h)) {
        // Edit walks every part of the set (the shortcut is on the first one), then back to Review
        const s = whole.get(h);
        out.push({ t: s.name, s: s.label, item: ss[0].dataset.slot, edit: codes(visibleMembers(s).flatMap(p => slotsOf(p).filter(x => !x.hidden))) });
        return;
      }
      if (inSet.has(h)) return;
      const chosen = ss.filter(s => picked(s) || blackLead(s));
      const need = ss.find(s => s.hasAttribute('data-required') && !picked(s));
      const text = chosen.length ? chosen.map(s => colorText(s)).join(' + ') : need ? '' : colorText(ss[0]);
      out.push({ t: h.dataset.name, s: text || 'Not picked yet', miss: !!need, item: (need || ss[0]).dataset.slot, edit: codes(ss) });
    });
    return out;
  }
  function renderReview() {
    const ul = $('[data-gsb-rows]');
    ul.textContent = '';
    const row = (id, t, s, miss, item, edit) => {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gsb-row' + (miss ? ' is-missing' : '');
      b.dataset.gsbGoto = id;
      if (item) b.dataset.gsbItem = item;
      b.dataset.gsbFromReview = edit || '*'; // the picks this row's Edit walks before going back to Review
      b.innerHTML = '<span class="gsb-row__t"></span><span class="gsb-row__s"></span><span class="gsb-row__e" aria-hidden="true"></span>';
      b.querySelector('.gsb-row__t').textContent = t;
      b.querySelector('.gsb-row__s').textContent = s;
      b.querySelector('.gsb-row__e').textContent = miss ? 'Fix' : 'Edit';
      b.setAttribute('aria-label', (miss ? 'Fix ' : 'Edit ') + t + ': ' + s);
      li.append(b);
      ul.append(li);
      return b;
    };
    let colorsRow = false;
    activeSteps().filter(p => p.dataset.gsbStep !== 'review').forEach(p => {
      const id = p.dataset.gsbStep;
      if (skipped(id)) {
        // matching photos: one "Colors" row stands for all the color steps
        if (!colorsRow) row('match', 'Colors', 'Built to match your photos', false);
        colorsRow = true;
        return;
      }
      const miss = missingIn(id).length > 0 && id !== 'model';
      if (id === 'match' && matching() && photos.length) {
        const n = photos.length;
        const b = row(id, p.dataset.title, n + (n > 1 ? ' photos' : ' photo') + (notesEl.value.trim() ? ' and your notes' : ''), miss);
        const strip = document.createElement('span');
        strip.className = 'gsb-pb-strip';
        photos.forEach(ph => {
          const im = document.createElement(ph.url ? 'img' : 'span');
          if (ph.url) { im.src = ph.thumb || ph.url; im.alt = ''; } else im.className = 'gsb-pb-strip__none';
          strip.append(im);
        });
        b.querySelector('.gsb-row__s').append(strip);
        return;
      }
      if (isColorStep(id)) {
        // the step's name over its parts
        const h = document.createElement('li');
        h.className = 'gsb-rows__h';
        h.textContent = p.dataset.title;
        ul.append(h);
        partRows(p).forEach(r => row(id, r.t, r.s, r.miss, r.item, r.edit));
        return;
      }
      row(id, p.dataset.title, stepSummary(id), miss);
    });
    const sn = $('[data-gsb-match-sharenote]');
    if (sn) sn.hidden = !matching();
  }
  function closeStepList() {
    stepList.hidden = true;
    $('[data-gsb-bar]')?.classList.remove('is-under');
    $$('[data-gsb-steps-toggle]').forEach(b => b.setAttribute('aria-expanded', 'false'));
  }
  $$('[data-gsb-steps-toggle]').forEach(b => b.addEventListener('click', () => {
    if (!phoneUI()) return;
    const open = stepList.hidden;
    stepList.hidden = !open;
    $('[data-gsb-bar]').classList.toggle('is-under', open);
    $$('[data-gsb-steps-toggle]').forEach(x => x.setAttribute('aria-expanded', String(open)));
    if (open) stepList.querySelector('[aria-current]')?.focus();
  }));
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    if (!stepList.hidden) { closeStepList(); $('.gsb-stephead__btn').focus(); }
    closeLocator();
  });
  document.addEventListener('click', e => {
    if (!stepList.hidden && !e.target.closest('[data-gsb-steplist], [data-gsb-steps-toggle]')) closeStepList();
  });
  root.addEventListener('click', e => {
    const g = e.target.closest('[data-gsb-goto]');
    if (!g) return;
    fromReview = g.dataset.gsbFromReview || '';
    const id = g.dataset.gsbGoto;
    go(id, { item: g.dataset.gsbItem || '' }); // a review row's Edit opens its part's pick; a step opens its first pick
    if (g.classList.contains('is-missing')) { const miss = missingIn(id); if (miss.length) reveal(miss[0].el); }
  });

  function scrollToBuilder(pane) {
    const hdr = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    // what scrolls: the page, or on wide screens the theme's .page-wrapper (base.css makes it the scroll container there)
    const doc = document.scrollingElement || document.documentElement;
    const pw = document.querySelector('.page-wrapper');
    const sc = pw && /(auto|scroll)/.test(getComputedStyle(pw).overflowY) ? pw : doc;
    const toGrid = gap => {
      const top = $('[data-gsb-grid]').getBoundingClientRect().top - (sc === doc ? 0 : sc.getBoundingClientRect().top);
      const target = sc.scrollTop + top - hdr - gap;
      if (Math.abs(sc.scrollTop - target) > 4) sc.scrollTo({ top: target, behavior: smooth() });
    };
    // phones: the picture pins under the header and the step starts right under it
    if (pinnedPhone()) toGrid(0);
    // computers: the builder's top (the steps overview) just under the header, where the sticky picture sits. Scrolling
    // to the step itself pushed the steps overview off the screen and, on a step shorter than the picture, the picture
    // up under the header, so it jumped up and down from one Next to the next.
    else if (!phoneUI()) toGrid(16);
    else {
      // a phone held sideways (the picture scrolls away with the page): the step's top just under the header. Measured,
      // not scrollIntoView: the page's scroll padding can't see the header height the theme sets on <body>, so the pick's
      // heading landed under the sticky header
      const r = pane.getBoundingClientRect();
      if (r.top < hdr || r.top > window.innerHeight * 0.45) {
        const target = sc.scrollTop + r.top - (sc === doc ? 0 : sc.getBoundingClientRect().top) - hdr - 8;
        sc.scrollTo({ top: target, behavior: smooth() });
      }
    }
  }
  // Show a step. In a color step, which pick: `item` (a slot code) when asked for; else a pick that must be made
  // (none today); else the first. `user`: moved there with Next or Back.
  function go(id, { push = true, focus = true, scroll = true, item = '', user = false } = {}) {
    closeStepList();
    clearToast();
    // a color step while matching photos (from the step list, or the browser's Back): the match step instead
    const wasSkipped = skipped(id);
    if (wasSkipped) id = 'match';
    const list = activeSteps();
    const pane = list.find(p => p.dataset.gsbStep === id) || list[0];
    current = pane.dataset.gsbStep;
    visited.add(current);
    panes.forEach(p => { const on = p === pane; p.classList.toggle('is-current', on); p.classList.toggle('is-off', !on); p.inert = !on; });
    const its = isColorStep(current) ? itemsOf(pane) : [];
    const slot = its.find(s => s.dataset.slot === item) || its.find(s => s.hasAttribute('data-required') && !picked(s)) || its[0] || null;
    sub = slot ? slot.dataset.slot : '';
    if (slot) showItem(pane, slot);
    if (current !== 'review') $('[data-gsb-linknote]').hidden = true;
    if (current === 'review') renderReview();
    renderNav();
    if (push) pushNav(stopKey(current, sub));
    else { navKeys[navAt] = stopKey(current, sub); saveUrl(); }
    if (scroll) scrollToBuilder(pane);
    if (focus) {
      if (pinnedPhone()) $('.gsb-stephead__btn').focus({ preventScroll: true });
      else ((slot && $('[data-gsb-itemhead]', pane)) || pane.querySelector('.gsb-pane__h')).focus({ preventScroll: true });
    }
    // Next or Back onto a part drawn on the bow: light it up for a moment, so people see which part it is
    if (user && slot && slot.dataset.index === '1') spotlight(holderOf(slot));
    if (wasSkipped) showToast('The color steps are skipped while we match your photos. To pick colors, choose “No, I’ll pick my colors”.');
  }
  async function goNext() {
    msg.textContent = '';
    if (photoJob && current === 'match') { nextBtn.disabled = true; await photoJob; nextBtn.disabled = false; }
    const nxt = nextStop();
    // a pick that must be made stops Next on that pick; the step's other picks are checked when leaving the step
    const leaving = !nxt || nxt.id !== current;
    const cs = curSlot();
    const miss = missingIn(current).filter(x => leaving || x.el === cs);
    if (miss.length) { flag(miss); renderNav(); return; }
    if (!nxt || nxt.id === 'review') { fromReview = ''; go('review'); return; }
    go(nxt.id, { item: nxt.sub, user: true });
  }
  // Back one pick (or step, or to Review after its Edit); the browser's own Back when that's where it would go, so the
  // history stays tidy and the phone's Back agrees with this one
  function goBack() {
    const prev = backStop();
    if (!prev) return;
    if (prev.id === 'review') fromReview = '';
    if (!backTo(prev)) go(prev.id, { item: prev.sub, user: true });
  }
  // A page of this visit's history that would show nothing new: a color pick while we match photos (those steps are
  // skipped), or the stop already on screen (a step opened twice, or a popup's page after a reload)
  const passable = key => !!key && (skipped(key.split('|')[0]) || key === stopKey(current, sub));
  // The browser's own Back, when it lands on `stop` (passing over pages that show nothing new); false = it wouldn't
  function backTo(stop) {
    const want = stopKey(stop.id, stop.sub);
    for (let j = navAt - 1; j >= 0; j -= 1) {
      if (navKeys[j] === want) { history.go(j - navAt); return true; }
      if (!passable(navKeys[j])) return false;
    }
    return false;
  }
  // add a page to the browser's history for what's on screen now (key: its stop, see navKeys)
  function pushNav(key) {
    navAt += 1;
    navKeys.length = navAt;
    navKeys[navAt] = key;
    history.pushState(navState(), '', hereUrl());
  }
  nextBtn.addEventListener('click', e => { if (current !== 'review') { e.preventDefault(); goNext(); } });
  backBtn.addEventListener('click', goBack);
  let passing = 0;  // pages passed over in a row (a safety stop)
  let passT = 0;
  // show a page of the history: its step and pick, and whether it came from Review's Edit
  function land(st) {
    passing = 0;
    fromReview = typeof st.gsbEdit === 'string' ? st.gsbEdit : '';
    // an older page state has no pick (gsbSub): its step opens on the first pick
    go(st.gsbStep, { push: false, item: st.gsbSub || '' });
  }
  window.addEventListener('popstate', e => {
    const st = e.state || {};
    const was = navAt;
    if (typeof st.gsbAt === 'number') navAt = st.gsbAt;
    const dir = navAt > was ? 1 : -1; // Forward or Back
    clearTimeout(passT);
    // the full-size view: the phone's Back closes it and stays on this pick; our own Back after Close does nothing more
    if (fullPop) { fullPop = false; return; }
    if (fullOpen()) { full.close(); return; }
    // the past-builds popup: Back shows all the photos again (from one big photo), then closes it
    // (this page of the history is from before the popup opened: saveUrl() puts the photos picked since on it)
    if (galPop) { galPop = false; saveUrl(); return; }
    if (galOpen()) {
      if (zoomAt >= 0) { closeZoom(); pushNav(stopKey(current, sub) + '|gal'); } else { gal.close(); saveUrl(); }
      return;
    }
    if (!stepList.hidden || !$('[data-gsb-locator]').hidden) {
      closeStepList();
      closeLocator();
      pushNav(stopKey(current, sub));
      return;
    }
    if (!st.gsbStep) return;
    // Pages that would show nothing new are passed over, the same way (Back or Forward), so each press of the phone's
    // Back shows something: after "Yes, match a past build", the color picks walked before it (up to 17 pages) would
    // each land on the match step again. Never back past this visit's first page.
    if (passable(stopKey(st.gsbStep, st.gsbSub || '')) && (dir > 0 || navAt > 0) && passing < 60) {
      passing += 1;
      history.go(dir);
      passT = setTimeout(() => land(st), 400); // Forward at the newest page: nowhere further to go, so show this one
      return;
    }
    land(st);
  });
  function flag(miss) {
    miss.forEach(x => setErr(x.el, x.text));
    reveal(miss[0].el);
  }
  // Show a field wherever it lives: its step and its pick (a custom length box: the step's last pick); then focus it
  function reveal(el) {
    const pane = el.closest('[data-gsb-step]');
    const slot = el.closest('[data-gsb-part]');
    const its = pane ? itemsOf(pane) : [];
    const item = slot ? slot.dataset.slot : customBox && customBox.contains(el) && its.length ? its[its.length - 1].dataset.slot : '';
    if (pane && (pane.dataset.gsbStep !== current || (item && item !== sub))) go(pane.dataset.gsbStep, { focus: false, item });
    const box = el.querySelector('[data-gsb-swatches]') || el;
    const target = (el === photosBox && fileIn) || box.querySelector('input:checked:not([disabled])') || box.querySelector('label:not(.is-filtered) input:not([disabled])') || el;
    target.focus({ preventScroll: true });
    el.scrollIntoView({ behavior: smooth(), block: 'start' });
  }

  /* ---------- "Where?": light the part up on the bow ---------- */
  function spotlight(panel) {
    const drawn = [firstSlot(panel)].filter(s => s && s.hasAttribute('data-drawn'));
    if (!drawn.length) return false;
    const urlFor = (s, v) => { const p = picked(s); const own = p ? (p.dataset.views || '').split('|')[v] : ''; return own || (layerUrls(s).find(u => u[v]) || [])[v] || ''; };
    let v = view;
    if (!drawn.some(s => urlFor(s, v))) { const alt = [0, 1, 2, 3].find(x => drawn.some(s => urlFor(s, x))); if (alt == null) return false; setView(alt); v = alt; }
    $$('.gsb-spot', canvas).forEach(i => i.remove());
    drawn.forEach(s => {
      const u = urlFor(s, v);
      if (!u) return;
      const img = document.createElement('img');
      img.className = 'gsb-spot';
      img.alt = '';
      img.src = sized(u);
      pic.append(img);
    });
    canvas.classList.add('is-spot');
    clearTimeout(spotlight.t);
    spotlight.t = setTimeout(() => { canvas.classList.remove('is-spot'); $$('.gsb-spot', canvas).forEach(i => i.remove()); }, reduced() ? 1500 : 1300);
    return true;
  }
  function closeLocator() { const l = $('[data-gsb-locator]'); if (l) l.hidden = true; }
  $('[data-gsb-locator-close]').addEventListener('click', closeLocator);

  /* ---------- "View full size" (Joey, Oct 20 2026): the bow standing up, full screen, so it can be seen cleanly ----------
     The main angle only for now: the model's base bow in the left view plus each picked color's left-view layer, as big
     as the screen needs (the left view's pictures are kept at 1920px for this: data/builder_full_size.py). It opens from
     the button on the picture or a tap on the picture, and adds a page to the history so the phone's Back button closes
     it; Close and Esc take that page back off. */
  const full = $('[data-gsb-full]');
  const fullPic = $('[data-gsb-full-pic]');
  const fullStage = $('[data-gsb-full-stage]');
  const fullWait = $('[data-gsb-full-wait]');
  const fullBtn = $('[data-gsb-full-open]');
  // the bow in the left view, as parts of the square picture (measured on the base drawings: x 510-768, y 96-1122 of 1200)
  const FULL_BOX = { x: 0.425, y: 0.08, w: 0.215, h: 0.855 };
  const fullOpen = () => !!full && full.open;
  let fullPop = false; // the history.back() after Close or Esc is ours, not a Back press
  let fullToken = 0;
  // as big as the screen allows, the bow centered (the square's sides are cropped)
  function layoutFull() {
    const W = fullStage.clientWidth;
    const H = fullStage.clientHeight;
    const pad = W < 600 ? 14 : 28;
    const s = Math.max(0, Math.min((H - 2 * pad) / FULL_BOX.h, (W - 2 * pad) / FULL_BOX.w));
    fullPic.style.width = fullPic.style.height = Math.round(s) + 'px';
    fullPic.style.left = Math.round(W / 2 - (FULL_BOX.x + FULL_BOX.w / 2) * s) + 'px';
    fullPic.style.top = Math.round(H / 2 - (FULL_BOX.y + FULL_BOX.h / 2) * s) + 'px';
  }
  // `push: false`: reopened after a reload (this page of the history is already the full view's)
  async function openFull({ push = true } = {}) {
    if (!full || fullOpen() || photoMode()) return;
    const token = ++fullToken;
    const m = model();
    const urls = [m.views[0]];
    layers.forEach(({ part }) => {
      const inp = !part.hidden && picked(part);
      const u = inp ? (inp.dataset.views || '').split('|')[0] : '';
      if (u) urls.push(u);
    });
    $('[data-gsb-full-title]', full).textContent = m.name + ' · your build';
    $('[data-gsb-full-note]', full).textContent = m.note || '';
    fullPic.setAttribute('aria-label', 'Picture of your ' + m.name + ', left view' + (picSaid ? ': ' + picSaid : ''));
    fullPic.textContent = '';
    fullPic.classList.add('is-loading');
    const waiting = setTimeout(() => { fullWait.hidden = false; }, 300); // "Loading…" only when it takes a moment
    full.showModal();
    layoutFull();
    // the pictures at the size the screen shows them (a few set sizes, so they're cached), up to the 1920px kept
    const need = fullPic.offsetWidth * (window.devicePixelRatio || 1);
    const px = [1200, 1600].find(w => w >= need) || 1920;
    const imgs = urls.filter(Boolean).map(u => {
      const img = document.createElement('img');
      img.alt = '';
      img.src = u.replace(/([?&])width=\d+/, '$1width=' + px);
      fullPic.append(img);
      return img;
    });
    if (push) pushNav(stopKey(current, sub) + '|full'); else saveUrl(); // navState() now says the full view is open (gsbFull)
    // show the bow once every layer is in, so it doesn't build up piece by piece
    await Promise.all(imgs.map(i => (i.decode ? i.decode().catch(() => {}) : null)));
    clearTimeout(waiting);
    if (token !== fullToken) return;
    fullPic.classList.remove('is-loading');
    fullWait.hidden = true;
  }
  if (full) {
    fullBtn?.addEventListener('click', openFull);
    full.querySelector('[data-gsb-full-close]').addEventListener('click', () => full.close());
    full.addEventListener('close', () => {
      fullToken += 1;
      // closed with Close or Esc: take the page we added back off the history (after the phone's Back, it's gone already)
      if (history.state && history.state.gsbFull) { fullPop = true; history.back(); }
    });
    window.addEventListener('resize', () => { if (fullOpen()) layoutFull(); });
  }
  form.addEventListener('click', e => {
    const w = e.target.closest('[data-gsb-where]');
    if (w) {
      const panel = w.closest('[data-gsb-panel]');
      if (!panel) return;
      const lit = spotlight(panel);
      const txt = panel.querySelector('[data-gsb-wheretext]');
      if (txt) {
        const open = txt.hidden;
        txt.textContent = '';
        if (open) {
          const p = document.createElement('p');
          p.textContent = panel.dataset.help || (lit ? 'Lit up in orange on the bow.' : 'Not drawn on the picture. Text us if you\'re not sure where it goes.');
          txt.append(p);
          if (panel.dataset.howto) { const a = document.createElement('a'); a.href = panel.dataset.howto; a.className = 'gsb-link'; a.textContent = 'How to swap this part ›'; txt.append(a); }
        }
        txt.hidden = !open;
        w.setAttribute('aria-expanded', String(open));
      }
      if (panel.dataset.locator) {
        const l = $('[data-gsb-locator]');
        $('[data-gsb-locator-img]').src = panel.dataset.locator;
        $('[data-gsb-locator-cap]').textContent = panel.dataset.name;
        l.hidden = false;
      }
      return;
    }
    const mr = e.target.closest('[data-gsb-matchriser]');
    if (mr) { const set = sets.find(s => s.box.contains(mr)); if (set) applySet(set, mr.dataset.key, mr.dataset.fin || undefined); return; }
    // "Use this color for all the hardware": this pick's color (and finish) on every part in the set
    const all = e.target.closest('[data-gsb-allset]');
    if (all && all.getAttribute('aria-disabled') !== 'true') {
      const set = sets.find(s => s.box.contains(all));
      const s = set && firstSlot(visibleMembers(set)[0]);
      if (s) { const i = picked(s); applySet(set, matchKey(i), isAlu(s) && !noFin(i) ? finOf(s) : undefined); }
    }
  });

  /* ---------- toast + Undo (stays until the next action) ---------- */
  function snapshot() {
    return {
      radios: slots.map(s => [s, $$('input[name$="--' + s.dataset.index + '"]', s).find(i => i.checked)?.value ?? '', s.dataset.fin || '']),
      fit: Object.keys(GROUP_LIST).map(g => [g, groupValue(g)]),
      model: model().handle, view, step: current, sub, visited: [...visited], linknote: !$('[data-gsb-linknote]').hidden,
      // "I want custom lengths": the tick, each box and the "can't be returned" tick, so Undo brings them back too
      custom: customBox ? { on: customOn(), lens: $$('[data-gsb-len]', customBox).map(i => i.value), ack: customBox.querySelector('[data-gs-custom-ack]').checked } : null,
      // "Match a past build": the choice, the photos and the description, so Undo after "Start over" brings them back
      match: matchPane ? { on: matching(), photos: photos.slice(), notes: notesEl.value, shown, note: matchNote } : null,
    };
  }
  function restore(snap) {
    const mi = form.querySelector('input[name="model"][value="' + CSS.escape(snap.model) + '"]');
    if (mi) mi.checked = true;
    applyModel();
    snap.fit.forEach(([g, v]) => { $$('input', groups[g]).forEach(i => { i.checked = i.value === v; }); });
    snap.radios.forEach(([s, v, f]) => {
      const i = $$('input[name$="--' + s.dataset.index + '"]', s).find(x => x.value === v);
      if (i) i.checked = true;
      if (f) s.dataset.fin = f; else delete s.dataset.fin;
    });
    setView(snap.view, true);
    if (snap.visited) snap.visited.forEach(v => visited.add(v));
    if (snap.linknote) $('[data-gsb-linknote]').hidden = false;
    if (customBox && snap.custom) {
      customBox.querySelector('[data-gs-custom-on]').checked = snap.custom.on;
      customBox.querySelector('[data-gs-custom-body]').hidden = !snap.custom.on;
      $$('[data-gsb-len]', customBox).forEach((i, n) => { i.value = snap.custom.lens[n] || ''; i.disabled = !snap.custom.on; });
      const ack = customBox.querySelector('[data-gs-custom-ack]');
      ack.disabled = !snap.custom.on;
      ack.checked = snap.custom.on && snap.custom.ack;
    }
    if (matchPane && snap.match) {
      setMatch(snap.match.on);
      photos.splice(0, photos.length, ...snap.match.photos);
      notesEl.value = snap.match.notes;
      shown = snap.match.shown;
      matchNote = snap.match.note || '';
      renderThumbs();
    }
    refresh();
    syncAllFinish();
    allTiles();
  }
  function showToast(text, snap) {
    toastSnap = snap || null;
    $('[data-gsb-undo]').hidden = !snap;
    toastEl.classList.add('is-on');
    requestAnimationFrame(() => { $('[data-gsb-toast-text]').textContent = text; });
  }
  function clearToast() {
    toastSnap = null;
    $('[data-gsb-toast-text]').textContent = '';
    $('[data-gsb-undo]').hidden = true;
    toastEl.classList.remove('is-on');
  }
  $('[data-gsb-undo]').addEventListener('click', () => {
    const snap = toastSnap;
    clearToast();
    if (!snap) return;
    restore(snap);
    if (snap.step !== current || (snap.sub || '') !== sub) go(snap.step, { item: snap.sub || '' });
  });

  /* ---------- the page address holds the build (other parameters, like ad tracking, are kept) ---------- */
  const FIN_KEY = s => s.dataset.slot + '-finish';
  // custom string and cable lengths: custom-string, custom-power-cable… (the page address and the cart's link back to the
  // bow, so a reload or that link brings them back; never a shared link: they're the customer's own numbers)
  const LEN_KEY = i => 'custom-' + slug(i.dataset.gsbLen);
  const lenBoxes = customBox ? $$('[data-gsb-len]', customBox) : [];
  const OWNED = ['model', 'hand', 'draw', 'weight', 'match', 'angle', ...new Set(slots.map(s => s.dataset.slot)), ...new Set(slots.filter(isAlu).map(FIN_KEY)), ...lenBoxes.map(LEN_KEY)];
  function params(keepOthers, colors = true, lengths = false) {
    const p = new URLSearchParams(keepOthers ? location.search : '');
    OWNED.forEach(k => p.delete(k));
    p.set('model', model().handle);
    activeGroups().forEach(g => { const v = groupValue(g); if (v) p.set(g, slug(v)); });
    if (matching()) p.set('match', '1'); // "Match a past build" is on (the photos never go in the address)
    if (colors) visible().forEach(s => {
      const i = picked(s);
      if (i) p.set(s.dataset.slot, i.value);
      if (isAlu(s) && !noFin(i) && finOf(s) !== finishesOf(s)[0]) p.set(FIN_KEY(s), slug(finOf(s)));
    });
    if (lengths && colors) lenBoxes.forEach(i => { if (!i.disabled && customOn() && i.value.trim()) p.set(LEN_KEY(i), i.value.trim()); });
    if (view) p.set('angle', String(view));
    return p;
  }
  // a shared link (and the order's _link): while matching photos, the model and specs only (no photos, no color picks)
  // plus match=1 (match=cart on the cart's link back to the bow), so it opens on the match step, not an all-black Review
  const buildPath = (kind, lengths) => {
    const p = params(false, !matching(), lengths);
    if (kind && matching()) p.set('match', kind);
    return location.pathname + '?' + p.toString();
  };
  const hereUrl = () => location.pathname + '?' + params(true, true, true).toString() + location.hash;
  // this page visit's own memory (kept through a reload, never in a link): the step and its pick, the page's number in
  // this visit (gsbAt), whether it came from Review's Edit (gsbEdit), the steps seen so far (the stepper's ticks), and
  // while matching photos, that match was on, how many of the customer's own photos there were (they can't come back),
  // the photos picked from our past builds (they can: just addresses), the description, the note, and which popup was open
  const navState = () => ({ ...(history.state || {}), gsbStep: current, gsbSub: sub, gsbAt: navAt, gsbEdit: fromReview, gsbSeen: [...visited],
    gsbMatch: matching(), gsbPhotos: matching() ? ownPhotos() : 0,
    gsbPast: matching() ? photos.filter(p => p.past) : [], gsbNotes: matching() && notesEl ? notesEl.value : '', gsbNote: matching() ? matchNote : '',
    gsbFull: fullOpen(), gsbGal: galOpen() });
  const saveUrl = () => history.replaceState(navState(), '', hereUrl());

  function applyModel() {
    const m = model();
    slots.forEach(p => { p.hidden = !m.parts.includes(p.dataset.gsbPart); });
    panels.forEach(p => { p.hidden = slotsOf(p).every(s => s.hidden); });
    const dropped = renderChips(m);
    placeSets();
    allTiles();
    return dropped;
  }

  function refresh() {
    syncCustom();
    summary();
    syncSets();
    renderReadouts();
    rawSwitches();
    renderNav();
    if (current === 'review') renderReview();
    renderStage();
    renderMatchNote();
    draw();
    saveUrl();
    shareMsg.textContent = '';
  }

  /* ---------- "Match a past build": photos of a bow we built instead of color picks ---------- */
  function setMatch(on) {
    if (!matchPane) return;
    const r = matchPane.querySelector('input[name="gsb-match"][value="' + (on ? 'yes' : 'no') + '"]');
    if (r) r.checked = true;
    matchBody.hidden = !on;
  }
  // Photos can't come back after a reload or through a link: say why they're missing (Next and Add to cart wait for new ones)
  const MATCH_NOTES = {
    reload: 'Your photos were cleared when the page reloaded. Please add them again.',
    // a reload brings back the photos picked from our past builds, not the customer's own
    reloadSome: 'The photos you added were cleared when the page reloaded. Please add them again. Your picks from our past builds are still here.',
    link: 'A link brings the model and specs, not the photos. Add photos of the bow you want matched, or pick “No, I’ll pick my colors”.',
    cart: 'Your photos are with this bow in your cart. To change them, remove the bow from your cart, then '
      + (hasPast ? 'pick from our past builds or add your photos again here.' : 'add your photos again here.'),
  };
  function renderMatchNote() {
    const el = $('[data-gsb-match-note]');
    if (!el) return;
    const key = matchNote === 'reloadSome' && !photos.some(p => p.past) ? 'reload' : matchNote; // the past-build picks were taken off since
    const on = matching() && !!MATCH_NOTES[key] && (key === 'cart' || (key === 'reloadSome' ? !ownPhotos() : !photos.length));
    if (on && el.dataset.note !== key) {
      el.textContent = MATCH_NOTES[key];
      if (matchNote === 'cart') {
        el.append(' ');
        const a = document.createElement('a');
        a.className = 'gsb-link';
        a.href = (window.Shopify?.routes?.root || '/') + 'cart';
        a.textContent = 'Go to your cart';
        el.append(a);
      }
      el.dataset.note = key;
    }
    el.hidden = !on;
  }
  function stageTitle() {
    const p = photoMode() ? photos[shown] : null;
    $('[data-gsb-stage-title]').textContent = p ? (p.past ? 'Our ' + p.model + ' · photo ' : 'Your photo ') + (shown + 1) + ' of ' + photos.length : model().name + ' · ' + VIEW_NAMES[view];
  }
  // The plate while matching: the customer's photo, with "Photo 1"… "Drawing" buttons in place of the bow's views
  function renderStage() {
    if (!matchPane) return;
    if (shown >= photos.length) shown = Math.max(0, photos.length - 1);
    const on = photoMode();
    canvas.classList.toggle('is-photo', on);
    if (fullBtn) fullBtn.hidden = on; // "View full size" is for the drawing, not the customer's photo
    shot.hidden = !on;
    const img = $('[data-gsb-shot-img]', shot);
    const none = $('[data-gsb-shot-none]', shot);
    const p = on ? photos[shown] : null;
    img.hidden = !(p && p.url);
    none.hidden = !(p && !p.url);
    if (p && p.url) {
      if (img.getAttribute('src') !== p.url) img.src = p.url;
      img.alt = (p.past ? 'Photo ' + (shown + 1) + ' of ' + photos.length + ': a ' + p.model + ' we built' : 'Your photo ' + (shown + 1) + ' of ' + photos.length) + ' (the bow to match)';
    }
    if (p && !p.url) none.textContent = p.name + ': no preview on this device, but we’ll get the photo.';
    const list = matching() && photos.length > 0;
    photoViews.hidden = !list;
    angleViews.hidden = list;
    if (list && photoViews.childElementCount !== photos.length + 1) {
      photoViews.textContent = '';
      [...photos.map((x, i) => ['Photo ' + (i + 1), i]), ['Drawing', -1]].forEach(([t, i]) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gsb-view';
        b.textContent = t;
        b.dataset.gsbPhoto = String(i);
        photoViews.append(b);
      });
    }
    $$('[data-gsb-photo]', photoViews).forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.gsbPhoto) === (on ? shown : -1))));
    $$('.gsb-pb-thumb', thumbs).forEach((li, i) => li.classList.toggle('is-on', on && i === shown));
    stageTitle();
  }
  const GRID = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3.5" y="3.5" width="7" height="7"/><rect x="13.5" y="3.5" width="7" height="7"/><rect x="3.5" y="13.5" width="7" height="7"/><rect x="13.5" y="13.5" width="7" height="7"/></svg>';
  const CAMERA = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 8.5h3l1.6-2.5h6.8L17 8.5h3a1 1 0 0 1 1 1V19a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1Z"/><circle cx="12" cy="13.5" r="3.6"/></svg>';
  function renderThumbs() {
    if (!matchPane) return;
    thumbs.textContent = '';
    photos.forEach((p, i) => {
      const li = document.createElement('li');
      li.className = 'gsb-pb-thumb' + (p.past ? ' is-past' : '');
      if (p.url) {
        const img = document.createElement('img');
        img.src = p.thumb || p.url;
        img.alt = 'Photo ' + (i + 1) + ': ' + p.name;
        li.append(img);
      } else {
        const s = document.createElement('span');
        s.className = 'gsb-pb-thumb__name';
        s.textContent = p.name;
        li.append(s);
      }
      const see = document.createElement('button');
      see.type = 'button';
      see.className = 'gsb-pb-thumb__see';
      see.dataset.gsbPhoto = String(i);
      see.setAttribute('aria-label', 'Show photo ' + (i + 1) + ' on the picture');
      const n = document.createElement('span');
      n.className = 'gsb-pb-thumb__n';
      n.setAttribute('aria-hidden', 'true');
      n.textContent = 'Photo ' + (i + 1);
      const x = document.createElement('button');
      x.type = 'button';
      x.className = 'gsb-pb-thumb__x';
      x.dataset.gsbPhotoX = String(i);
      x.setAttribute('aria-label', 'Remove photo ' + (i + 1) + ' (' + p.name + ')');
      x.textContent = '×';
      li.append(see, n, x);
      thumbs.append(li);
    });
    // "Pick from our past builds" (opens the popup) next to "Add photos" (the customer's own)
    if (hasPast && photos.length < MAX_PHOTOS) {
      const li = document.createElement('li');
      li.className = 'gsb-pb-add gsb-pb-gal' + (photos.length ? '' : ' gsb-pb-add--wide');
      li.innerHTML = '<button type="button" data-gsb-gal-open aria-haspopup="dialog" aria-controls="' + gal.id + '">' + GRID + '<span class="gsb-pb-add__t"></span><span class="gsb-pb-add__s"></span></button>';
      $('.gsb-pb-add__t', li).textContent = photos.length ? 'Our builds' : 'Pick from our past builds';
      $('.gsb-pb-add__s', li).textContent = photos.length ? 'Pick more' : 'Photos of bows we’ve built.';
      thumbs.append(li);
    }
    if (photos.length < MAX_PHOTOS) {
      const li = document.createElement('li');
      li.className = 'gsb-pb-add' + (photos.length ? '' : ' gsb-pb-add--wide');
      li.innerHTML = '<label for="' + fileIn.id + '">' + CAMERA + '<span class="gsb-pb-add__t"></span><span class="gsb-pb-add__s"></span></label>';
      $('.gsb-pb-add__t', li).textContent = photoJob ? 'Getting ready…' : photos.length ? 'Add' : hasPast ? 'Add your photos' : 'Add photos';
      $('.gsb-pb-add__s', li).textContent = photos.length ? (MAX_PHOTOS - photos.length) + ' more' : 'Take a photo or pick from your phone.' + (hasPast ? '' : ' Up to ' + MAX_PHOTOS + '.');
      thumbs.append(li);
    }
    fileIn.disabled = photos.length >= MAX_PHOTOS || !!photoJob; // off while photos are being made ready
    $('[data-gsb-photo-count]').textContent = photoJob ? 'Getting your photos ready…' : photos.length ? photos.length + ' of ' + MAX_PHOTOS : '';
    if (photos.length) photosBox.classList.remove('is-missing');
  }
  // A photo ready to send: big phone photos are shrunk (same as the "Send us your bow" form, gs-service.js)
  const loadImage = file => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
    img.src = url;
  });
  async function prep(file) {
    if (file.type && !/^image\//.test(file.type)) throw new Error(file.name + ' isn\'t a photo.');
    let src = null;
    let w = 0;
    let h = 0;
    let tmpUrl = '';
    try {
      if (window.createImageBitmap) { src = await createImageBitmap(file, { imageOrientation: 'from-image' }); w = src.width; h = src.height; }
    } catch (e) { src = null; }
    if (!src) {
      try { const r = await loadImage(file); src = r.img; tmpUrl = r.url; w = src.naturalWidth; h = src.naturalHeight; } catch (e) { src = null; }
    }
    if (!src || !w || !h) {
      // can't read it here (e.g. a HEIC photo on some computers): send it as it is if it's small enough
      if (file.size <= MAX_BYTES) return { file, url: '', name: file.name };
      throw new Error(file.name + ' is too big to send. Try a smaller photo, or text it to us at ' + phone + '.');
    }
    const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
    if (scale === 1 && file.size <= KEEP_BYTES && /^image\/(jpeg|png|webp)$/.test(file.type)) {
      if (src.close) src.close();
      if (tmpUrl) URL.revokeObjectURL(tmpUrl);
      return { file, url: URL.createObjectURL(file), name: file.name };
    }
    const cw = Math.round(w * scale);
    const ch = Math.round(h * scale);
    const cv = document.createElement('canvas');
    cv.width = cw;
    cv.height = ch;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(src, 0, 0, cw, ch);
    if (src.close) src.close();
    if (tmpUrl) URL.revokeObjectURL(tmpUrl);
    const blob = await new Promise(res => cv.toBlob(res, 'image/jpeg', 0.85));
    if (!blob) {
      if (file.size <= MAX_BYTES) return { file, url: '', name: file.name };
      throw new Error(file.name + ' is too big to send. Try a smaller photo.');
    }
    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    const out = new File([blob], name, { type: 'image/jpeg' });
    return { file: out, url: URL.createObjectURL(out), name };
  }
  // The cart line for a match order: the builder's all-Solid version, specs, photos (as files) and the description
  function matchForm(m, v) {
    const fd = new FormData();
    fd.append('id', String(v.id));
    fd.append('quantity', '1');
    const P = (k, val) => { if (val != null && String(val).trim() !== '') fd.append('properties[' + k + ']', String(val).trim()); };
    activeGroups().forEach(g => P(groups[g].dataset.label, groupValue(g) + (groups[g].dataset.unit || '')));
    P('Colors', 'Match my photos');
    P('Match a past build', 'Yes. Any extra cost is invoiced before we build.');
    // the customer's own photos upload as files; a photo picked from our past builds is its https address (same key)
    photos.forEach((p, i) => { if (p.past) fd.append('properties[Match photo ' + (i + 1) + ']', p.link); else fd.append('properties[Match photo ' + (i + 1) + ']', p.file, p.name); });
    P('Match notes', notesEl.value);
    const link = buildPath('cart');
    P('_match', String(photos.length)); // the build sheet's "MATCH PHOTO – check price" flag
    P('_builder', JSON.stringify({ model: m.handle, hand: groupValue('hand'), draw: groupValue('draw'), weight: groupValue('weight'),
      colors: {}, finish: {}, black: [], match: true, link }));
    P('_link', link);
    return fd;
  }
  if (matchPane) {
    matchPane.addEventListener('change', e => {
      if (e.target.name !== 'gsb-match') return;
      clearToast();
      msg.textContent = '';
      const on = matching();
      matchBody.hidden = !on;
      setErr(photosBox, '');
      if (!on) { fromReview = ''; matchNote = ''; } // picking colors: Next goes on to the color steps
      else if (hasPast) loadPast().catch(() => {}); // the popup's photo list, ready before it's opened
      else if (customLengths().length) showToast('Your custom string and cable lengths aren’t used when we match your photos. Put them in the description if you need them.');
      refresh();
    });
    fileIn.addEventListener('change', () => {
      const files = [...fileIn.files];
      fileIn.value = '';
      if (photoJob || !files.length) return; // the box is turned off while photos are being made ready
      clearToast();
      setErr(photosBox, '');
      const room = MAX_PHOTOS - photos.length;
      const msgs = files.length > room ? ['Up to ' + MAX_PHOTOS + ' photos. ' + (room > 0 ? 'We added the first ' + room + '.' : 'Remove one to add another.')] : [];
      if (room <= 0) { $('[data-gsb-err]', photosBox).textContent = msgs.join(' '); return; }
      const before = photos.length;
      let finish;
      photoJob = new Promise(res => { finish = res; }); // Next and Add to cart wait for this
      renderThumbs();
      (async () => {
        try {
          for (const f of files.slice(0, room)) {
            if (photos.length >= MAX_PHOTOS) break;
            try { photos.push(await prep(f)); } catch (err) { msgs.push(err.message); }
          }
        } finally {
          photoJob = null;
          finish();
          if (photos.length > before) shown = photos.length - 1; // the newest photo goes on the plate
          renderThumbs();
          $('[data-gsb-err]', photosBox).textContent = msgs.join(' ');
          refresh();
        }
      })();
    });
    thumbs.addEventListener('click', e => {
      if (e.target.closest('[data-gsb-gal-open]')) { openGallery(); return; }
      const x = e.target.closest('[data-gsb-photo-x]');
      if (!x) return;
      clearToast();
      const i = Number(x.dataset.gsbPhotoX);
      const [gone] = photos.splice(i, 1);
      if (gone && gone.url && !gone.past) URL.revokeObjectURL(gone.url);
      if (shown > i || shown >= photos.length) shown = Math.max(0, shown - 1);
      setErr(photosBox, '');
      renderThumbs();
      refresh();
      ($$('[data-gsb-photo-x]', thumbs)[Math.min(i, photos.length - 1)] || fileIn).focus();
    });
    notesEl.addEventListener('change', () => { summary(); saveUrl(); }); // the review's build sheet lists the description; a reload keeps it
    // a phone may drop the page while the customer is off finding photos: keep the description they typed
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden' && matching()) saveUrl(); });
    renderThumbs();
  }

  /* ---------- "Pick from our past builds" (Joey, Oct 20 2026: "a popup to view all the bow pictures and select any of
     them. i have tons") ----------
     The photos are the past_build entries, as JSON from sections/gs-past-builds-json.liquid (up to 250 a page), loaded
     once: when the customer picks "Yes" on the match step, or when the popup opens. They show in their sort order, with
     a filter by model that starts on the customer's model. A tap on a photo picks it (or takes it off): it joins the
     customer's own photos (4 in all), shows on the match step and on the picture at once, and goes on the cart line as
     "Match photo N" = its https address. The magnifier shows one photo big, with previous/next (or a swipe) and Pick.
     The popup adds a page to the history, so the phone's Back closes it (a big photo first); Close, Done and Esc take
     that page back off. */
  const galGrid = $('[data-gsb-gal-grid]');
  const galFilters = $('[data-gsb-gal-filters]');
  const galWait = $('[data-gsb-gal-wait]');
  const galCount = $('[data-gsb-gal-count]');
  const galBody = $('[data-gsb-gal-body]');
  const zoomEl = $('[data-gsb-gal-zoom]');
  const zoomImg = $('[data-gsb-gal-zimg]');
  const zoomPick = $('[data-gsb-gal-zpick]');
  let pastAll = null;  // every past build, in order: { h, model, cap, sort, w, ht, thumb, big, link }
  let pastJob = null;  // the list being loaded
  let galFilter = null; // the model shown ('' = all; null = not chosen yet: the customer's model)
  let galFor = '';     // the model the filter was chosen for
  let galShown = [];   // the photos in the grid now
  let zoomAt = -1;     // the photo shown big (its place in galShown); -1 = none
  let galAt = 0;       // the photo the keyboard is on: the grid is one stop for Tab (its photo and magnifier), arrows move
  function galOpen() { return !!gal && gal.open; }
  // "Click" with a mouse, "Tap" on a touch screen
  const tapWord = () => (window.matchMedia('(hover: hover) and (pointer: fine)').matches ? 'Click' : 'Tap');
  function loadPast() {
    if (!pastJob) {
      pastJob = (async () => {
        const out = [];
        // page 1, then each next page (Shopify names the page parameter: the JSON says which)
        for (let page = 1, param = '', n = 0; page && n < 40; n += 1) {
          const q = new URLSearchParams({ section_id: 'gs-past-builds-json' });
          if (param) q.set(param, String(page));
          const res = await fetch(location.pathname + '?' + q, { headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const data = JSON.parse(doc.querySelector('[data-gsb-past-json]').textContent);
          out.push(...data.items.filter(x => x && x.h && x.thumb && /^https:\/\//.test(x.link || '') && !out.some(o => o.h === x.h)));
          page = data.next;
          param = data.param || 'page';
        }
        pastAll = out.sort((a, b) => a.sort - b.sort);
        return pastAll;
      })().catch(err => { pastJob = null; throw err; }); // try again next time
    }
    return pastJob;
  }
  const pickedAt = h => photos.findIndex(p => p.past === h);
  const pastSaid = (p, i, n) => 'A ' + p.model + ' we built, photo ' + (i + 1) + ' of ' + n + (p.cap ? ': ' + p.cap : '');
  function galSay(text) { galCount.textContent = text; }
  function galTally() {
    const n = photos.length;
    const own = ownPhotos();
    galSay(n ? n + ' of ' + MAX_PHOTOS + ' photos picked' + (own ? ' (' + own + ' of yours)' : '') : tapWord() + ' a bow to pick it');
  }
  // the model names that have photos, the builder's models first
  function galModels() {
    const names = [...models.map(m => m.name), ...pastAll.map(p => p.model)];
    return names.filter((x, i) => names.indexOf(x) === i && pastAll.some(p => p.model === x));
  }
  function renderGallery() {
    const names = galModels();
    // the filter starts on the customer's model (when we have photos of it), and again after they change model
    if (galFilter === null || galFor !== model().handle) galFilter = names.includes(model().name) ? model().name : '';
    galFor = model().handle;
    if (galFilter && !names.includes(galFilter)) galFilter = '';
    galFilters.textContent = '';
    galFilters.hidden = names.length < 2;
    [['', 'All', pastAll.length], ...names.map(x => [x, x, pastAll.filter(p => p.model === x).length])].forEach(([v, t, n]) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gsb-gal__filter';
      b.dataset.gsbGalFilter = v;
      b.setAttribute('aria-pressed', String(v === galFilter));
      b.innerHTML = '<span></span><span class="gsb-gal__filter-n gsm-num"></span>';
      b.firstChild.textContent = t;
      b.lastChild.textContent = String(n);
      galFilters.append(b);
    });
    galShown = pastAll.filter(p => !galFilter || p.model === galFilter);
    galGrid.textContent = '';
    galShown.forEach((p, i) => {
      const li = document.createElement('li');
      li.className = 'gsb-gal__item';
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gsb-gal__pick';
      b.dataset.gsbGalPick = p.h;
      b.setAttribute('aria-label', pastSaid(p, i, galShown.length));
      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = p.w;
      img.height = p.ht;
      img.src = p.thumb;
      const tick = document.createElement('span');
      tick.className = 'gsb-gal__tick';
      tick.setAttribute('aria-hidden', 'true');
      b.append(img, tick);
      if (p.cap) { const c = document.createElement('span'); c.className = 'gsb-gal__cap'; c.textContent = p.cap; c.setAttribute('aria-hidden', 'true'); b.append(c); }
      const see = document.createElement('button');
      see.type = 'button';
      see.className = 'gsb-gal__see';
      see.dataset.gsbGalSee = String(i);
      see.setAttribute('aria-label', 'See photo ' + (i + 1) + ' bigger');
      see.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5 21 21M10.5 7.5v6M7.5 10.5h6"/></svg>';
      li.append(b, see);
      galGrid.append(li);
    });
    galRove(0);
    galMarks();
  }
  // Put the grid's one Tab stop on photo i (its tile and its magnifier); the rest are reached with the arrow keys
  function galRove(i, focus) {
    const picks = $$('[data-gsb-gal-pick]', galGrid);
    if (!picks.length) return;
    galAt = Math.max(0, Math.min(picks.length - 1, i));
    picks.forEach((b, n) => { const t = n === galAt ? 0 : -1; b.tabIndex = t; b.parentElement.querySelector('[data-gsb-gal-see]').tabIndex = t; });
    if (focus) picks[galAt].focus();
  }
  // which photos are picked (and as which photo number), on the grid and the big photo
  function galMarks() {
    const full = photos.length >= MAX_PHOTOS;
    gal.classList.toggle('is-full', full);
    $$('[data-gsb-gal-pick]', galGrid).forEach(b => {
      const at = pickedAt(b.dataset.gsbGalPick);
      b.setAttribute('aria-pressed', String(at >= 0));
      b.parentElement.classList.toggle('is-on', at >= 0);
      b.querySelector('.gsb-gal__tick').textContent = at >= 0 ? 'Photo ' + (at + 1) : '';
    });
    if (zoomAt >= 0) {
      const at = pickedAt(galShown[zoomAt].h);
      zoomPick.setAttribute('aria-pressed', String(at >= 0));
      zoomPick.textContent = at >= 0 ? '✓ Picked (photo ' + (at + 1) + ')' : full ? 'You have ' + MAX_PHOTOS + ' photos' : 'Pick this bow';
      zoomPick.title = at >= 0 ? tapWord() + ' again to take it off' : '';
      // not disabled (that drops the keyboard out of the popup, and the arrow keys with it): a press says why instead
      zoomPick.setAttribute('aria-disabled', String(at < 0 && full));
    }
    galTally();
  }
  function togglePast(h) {
    const at = pickedAt(h);
    if (at >= 0) {
      photos.splice(at, 1);
      if (shown > at || shown >= photos.length) shown = Math.max(0, shown - 1);
    } else if (photos.length >= MAX_PHOTOS) {
      galSay('You have ' + MAX_PHOTOS + ' photos, the most we take. ' + tapWord() + ' a picked one to take it off first.');
      return;
    } else {
      const p = pastAll.find(x => x.h === h);
      if (!p) return;
      photos.push({ past: p.h, model: p.model, cap: p.cap, url: p.big, thumb: p.thumb, link: p.link, name: 'a ' + p.model + ' we built' });
      shown = photos.length - 1; // the newest photo goes on the picture
    }
    clearToast();
    setErr(photosBox, '');
    renderThumbs();
    refresh();
    galMarks();
  }
  function showZoom(i) {
    if (!galShown.length) return;
    zoomAt = (i + galShown.length) % galShown.length;
    const p = galShown[zoomAt];
    zoomImg.src = p.big;
    zoomImg.alt = pastSaid(p, zoomAt, galShown.length);
    $('[data-gsb-gal-zn]', zoomEl).textContent = (zoomAt + 1) + ' of ' + galShown.length;
    $('[data-gsb-gal-zcap]', zoomEl).textContent = 'A ' + p.model + ' we built' + (p.cap ? ': ' + p.cap : '');
    const was = zoomEl.hidden;
    zoomEl.hidden = false;
    galBehind(true);
    gal.classList.add('is-zoom');
    galMarks();
    if (was) zoomPick.focus();
  }
  // while one photo is big, the rest of the popup behind it can't be reached
  function galBehind(on) {
    [...gal.children].forEach(c => { if (c !== zoomEl) c.inert = on; });
    gal.classList.toggle('is-zoom', on);
  }
  function closeZoom() {
    if (zoomAt < 0) return;
    const at = zoomAt;
    zoomAt = -1;
    zoomEl.hidden = true;
    galBehind(false);
    zoomImg.removeAttribute('src');
    $$('[data-gsb-gal-see]', galGrid)[at]?.focus(); // back where they were in the grid
  }
  async function openGallery({ push = true } = {}) {
    if (!gal || galOpen()) return;
    clearToast();
    gal.showModal();
    if (push) pushNav(stopKey(current, sub) + '|gal'); else saveUrl(); // navState() now says the popup is open (gsbGal)
    if (!pastAll) {
      galWait.textContent = 'Loading photos…';
      galWait.hidden = false;
      galTally();
      try { await loadPast(); } catch (err) {
        galWait.textContent = 'The photos didn’t load. Check your connection, then close this and try again. You can also add your own photos.';
        return;
      }
      if (!galOpen()) return;
    }
    galWait.hidden = !!pastAll.length;
    if (!pastAll.length) galWait.textContent = 'No photos here yet. Add your own photos instead.';
    const lead = $('[data-gsb-gal-lead]', gal);
    if (lead) lead.textContent = tapWord() + ' a bow to pick it, and again to take it off. Up to ' + MAX_PHOTOS + ' photos in all, yours included.';
    renderGallery();
    galBody.scrollTop = 0;
  }
  if (gal) {
    $('[data-gsb-gal-close]', gal).addEventListener('click', () => gal.close());
    $('[data-gsb-gal-done]', gal).addEventListener('click', () => gal.close());
    $('[data-gsb-gal-zback]', gal).addEventListener('click', closeZoom);
    zoomPick.addEventListener('click', () => { if (zoomAt >= 0) togglePast(galShown[zoomAt].h); });
    $$('[data-gsb-gal-zstep]', gal).forEach(b => b.addEventListener('click', () => showZoom(zoomAt + Number(b.dataset.gsbGalZstep))));
    galFilters.addEventListener('click', e => {
      const f = e.target.closest('[data-gsb-gal-filter]');
      if (!f || f.dataset.gsbGalFilter === galFilter) return;
      galFilter = f.dataset.gsbGalFilter;
      renderGallery();
      galBody.scrollTop = 0;
      $('[data-gsb-gal-filter][aria-pressed="true"]', galFilters)?.focus();
    });
    // arrow keys move between the photos (up and down by a row), Home and End to the first and last
    galGrid.addEventListener('keydown', e => {
      const li = e.target.closest('.gsb-gal__item');
      if (!li || e.altKey || e.ctrlKey || e.metaKey) return;
      const items = $$('.gsb-gal__item', galGrid);
      const i = items.indexOf(li);
      const cols = items.filter(x => x.offsetTop === items[0].offsetTop).length || 1;
      const to = { ArrowRight: i + 1, ArrowLeft: i - 1, ArrowDown: i + cols, ArrowUp: i - cols, Home: 0, End: items.length - 1 }[e.key];
      if (to == null) return;
      e.preventDefault();
      if (to >= 0 && to < items.length) galRove(to, true);
    });
    galGrid.addEventListener('focusin', e => {
      const li = e.target.closest('.gsb-gal__item');
      if (li) galRove($$('.gsb-gal__item', galGrid).indexOf(li));
    });
    galGrid.addEventListener('click', e => {
      const see = e.target.closest('[data-gsb-gal-see]');
      if (see) { showZoom(Number(see.dataset.gsbGalSee)); return; }
      const b = e.target.closest('[data-gsb-gal-pick]');
      if (b) togglePast(b.dataset.gsbGalPick);
    });
    // Esc on a big photo goes back to all the photos; on the grid it closes the popup
    gal.addEventListener('cancel', e => { if (zoomAt >= 0) { e.preventDefault(); closeZoom(); } });
    gal.addEventListener('keydown', e => {
      if (zoomAt >= 0 && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        showZoom(zoomAt + (e.key === 'ArrowLeft' ? -1 : 1));
      }
    });
    let zx = null;
    let zy = null;
    zoomImg.addEventListener('pointerdown', e => { if (e.pointerType !== 'mouse') { zx = e.clientX; zy = e.clientY; } });
    zoomImg.addEventListener('pointerup', e => {
      if (zx == null) return;
      const dx = e.clientX - zx;
      const dy = e.clientY - zy;
      zx = zy = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) showZoom(zoomAt + (dx < 0 ? 1 : -1)); // swipe to the next photo
    });
    gal.addEventListener('close', () => {
      if (zoomAt >= 0) { zoomAt = -1; zoomEl.hidden = true; galBehind(false); }
      // closed with Close, Done or Esc: take the page we added back off the history (after the phone's Back, it's gone already)
      if (history.state && history.state.gsbGal) { galPop = true; history.back(); }
      // the button that opened it was redrawn while picking: put the keyboard on the match step's photos
      ($('[data-gsb-gal-open]', thumbs) || $('[data-gsb-photo-x]', thumbs) || fileIn).focus({ preventScroll: true });
    });
  }

  /* ---------- one-time setup: finish switches ---------- */
  slots.forEach(s => finishSwitch(s, $('[data-gsb-swatches]', s)));

  /* ---------- restore a shared build ---------- */
  (() => {
    const q = new URLSearchParams(location.search);
    const mi = q.get('model') && form.querySelector('input[name="model"][value="' + CSS.escape(q.get('model')) + '"]');
    if (mi) mi.checked = true;
    applyModel();
    Object.keys(GROUP_LIST).forEach(g => {
      const want = q.get(g);
      if (!want) return;
      const hit = [...groups[g].querySelectorAll('input')].find(i => slug(i.value) === want);
      if (hit) hit.checked = true;
      else if (!groups[g].hidden) setErr(groups[g], 'The ' + model().name + ' doesn\'t come in a ' + want + (groups[g].dataset.unit || '') + ' ' + groups[g].dataset.label.toLowerCase() + '. Please pick again.');
    });
    let any = false;
    slots.forEach(p => {
      const want = q.get(p.dataset.slot);
      // a link made before Black was folded into the default names the part's own black color
      const hit = want && !p.hidden && (p.querySelector('input[value="' + CSS.escape(want) + '"]') || p.querySelector('input[value=""][data-black="' + CSS.escape(want) + '"]'));
      if (hit) { hit.checked = true; any = true; }
      const f = isAlu(p) && !p.hidden && q.get(FIN_KEY(p));
      const fin = f && finishesOf(p).find(x => slug(x) === f);
      if (fin) { p.dataset.fin = fin; any = true; }
    });
    // custom lengths (a reload, or the cart's link back to the bow): the boxes come back filled in; the "can't be
    // returned" tick has to be made again before Add to cart
    syncCustom(); // this model's boxes only
    const lens = lenBoxes.map(i => [i, q.get(LEN_KEY(i)) || '']).filter(([i, val]) => val && !i.closest('[data-gsb-custom-for]')?.hidden);
    if (lens.length) {
      customBox.querySelector('[data-gs-custom-on]').checked = true;
      customBox.querySelector('[data-gs-custom-body]').hidden = false;
      customBox.querySelector('[data-gs-custom-ack]').disabled = false;
      lenBoxes.forEach(i => { i.disabled = false; }); // as ticking the box does (gstring.js)
      lens.forEach(([i, val]) => { i.value = val.slice(0, 20); });
      syncCustom(); // the other model's boxes off again
      any = true;
    }
    placeSets();
    const v = parseInt(q.get('angle'), 10);
    if (Number.isInteger(v) && v >= 0 && v < 4) setView(v, true);
    summary();
    syncSets();
    // Where to land: same step after a reload; Review for a shared build; otherwise the first step
    const fit = ['hand', 'draw', 'weight'].some(k => q.get(k));
    const saved = history.state || {};
    let start = saved.gsbStep;
    // "Match a past build" was on (before a reload, or in a shared link or the cart's link back to the bow): the photos
    // can't come back, so pick Yes again and land on the match step with a note, never on an all-black Review
    const mq = q.get('match');
    const wasMatch = typeof saved.gsbMatch === 'boolean' ? saved.gsbMatch : mq === '1' || mq === 'cart';
    if (matchPane && wasMatch) {
      setMatch(true);
      if (!saved.gsbStep) matchNote = mq === 'cart' ? 'cart' : 'link';
      else {
        if (typeof saved.gsbNotes === 'string') notesEl.value = saved.gsbNotes;
        // the photos picked from our past builds come back (they're only addresses); the customer's own can't
        const past = hasPast && Array.isArray(saved.gsbPast) ? saved.gsbPast.filter(x => x && x.past && x.url && /^https:\/\//.test(x.link || '')) : [];
        photos.push(...past.slice(0, MAX_PHOTOS));
        renderThumbs();
        const kept = ['reload', 'reloadSome', 'link', 'cart'].includes(saved.gsbNote) ? saved.gsbNote : '';
        matchNote = kept === 'cart' ? 'cart' : saved.gsbPhotos > 0 ? (photos.length ? 'reloadSome' : 'reload') : kept;
      }
      if (!start || start === 'review') start = 'match';
      if (start === 'match') activeSteps().forEach(p => { if (p.dataset.gsbStep === 'model' || p.dataset.gsbStep === 'specs') visited.add(p.dataset.gsbStep); });
    }
    if (!start && (fit || any)) { start = 'review'; openedFromLink = true; }
    if (start === 'review') activeSteps().forEach(p => visited.add(p.dataset.gsbStep));
    // a reload: this page's number in the visit, the steps seen (the stepper's ticks), and Review's Edit if it came from there
    if (typeof saved.gsbAt === 'number') navAt = saved.gsbAt;
    if (Array.isArray(saved.gsbSeen)) saved.gsbSeen.forEach(id => { if (paneOf(id)) visited.add(id); });
    if (typeof saved.gsbEdit === 'string' && start === saved.gsbStep) fromReview = saved.gsbEdit;
    go(start || 'model', { push: false, focus: false, scroll: false, item: start === saved.gsbStep ? saved.gsbSub || '' : '' });
    // reloaded with the past-builds popup or the full-size view open: open it again (this page of the history is the
    // popup's, so Back closes it; left closed, the first Back would seem to do nothing)
    if (saved.gsbGal && hasPast && matching() && current === 'match') openGallery({ push: false });
    else if (saved.gsbFull && !photoMode()) openFull({ push: false });
    if (openedFromLink) $('[data-gsb-linknote]').hidden = false;
    refresh();
    if ([...q.keys()].some(k => OWNED.includes(k))) saveUrl(); // tidy a shared link; leave a plain visit's address alone
  })();

  let arrowAt = 0;
  let shuffling = false; // moving focus around on purpose (below), not the user leaving the switch
  const pendingFin = { v: null, t: 0 };
  function flushFin() {
    clearTimeout(pendingFin.t);
    const p = pendingFin.v;
    pendingFin.v = null;
    if (p) { clearToast(); applyFinish(p.holder, p.fin); }
  }
  form.addEventListener('keydown', e => { if (/^Arrow/.test(e.key) && e.target.closest?.('.gsb-finish')) arrowAt = performance.now(); });
  form.addEventListener('focusout', e => { if (!shuffling && pendingFin.v && e.target.closest?.('.gsb-finish') && !e.relatedTarget?.closest?.('.gsb-finish')) flushFin(); });
  form.addEventListener('change', e => {
    const t = e.target;
    if (matchPane && matchPane.contains(t)) return; // the match step has its own handlers (below)
    const holder0 = t.closest('[data-gsb-part]');
    if (t.name && t.name.startsWith('gsb-finish-')) {
      const holder = t.closest('.gsb-finish').parentElement;
      if (isAlu(holder)) {
        // A tap applies the finish at once. Arrow keys move through the tabs, so a keyboard user passing
        // Powder coat and Cerakote on the way to Pattern only browses: the finish lands once they stop on it.
        clearTimeout(pendingFin.t);
        pendingFin.v = null;
        if (t.value !== 'Pattern') {
          if (performance.now() - arrowAt < 400) { pendingFin.v = { holder, fin: t.value }; pendingFin.t = setTimeout(flushFin, 900); }
          else { clearToast(); applyFinish(holder, t.value); }
        }
      }
      filterFinish(holder, t.value);
      allTiles();
      // a radio group whose checked radio is now hidden drops out of the Tab order; touch the first visible one
      const box = $('[data-gsb-swatches]', holder);
      if (!box.querySelector('label:not(.is-filtered) input:checked')) {
        const first = box.querySelector('label:not(.is-filtered) input');
        if (first) { shuffling = true; first.tabIndex = 0; first.focus({ preventScroll: true }); t.focus({ preventScroll: true }); shuffling = false; }
      }
      return;
    }
    clearToast();
    msg.textContent = '';
    const holder = t.closest('[data-gsb-group], [data-gsb-part]');
    if (holder) setErr(holder, '');
    if (t.name === 'model') {
      Object.values(groups).forEach(g => setErr(g, ''));
      applyModel().forEach(([g, text]) => setErr(groups[g], text));
    }
    // a color only pictured in the left view: turn the bow to the left view so they see it
    if (holder0 && holder0.hasAttribute('data-drawn') && t.value) {
      const urls = (t.dataset.views || '').split('|');
      if (!urls[view] && urls[0]) setView(0, true);
    }
    refresh();
  });
  root.addEventListener('click', e => {
    const b = e.target.closest('[data-gsb-view]');
    if (b) setView(Number(b.dataset.gsbView));
    const ph = e.target.closest('[data-gsb-photo]'); // "Photo 1"… / "Drawing" on the plate while matching photos
    if (ph) { shown = Number(ph.dataset.gsbPhoto); refresh(); }
  });
  // swipe the picture to turn the bow (the view buttons do the same)
  let sx = null;
  let sy = null;
  let swiped = false; // a swipe isn't a tap: it turns the bow, it doesn't open the full-size view
  canvas.addEventListener('pointerdown', e => { swiped = false; if (e.pointerType !== 'mouse') { sx = e.clientX; sy = e.clientY; } });
  canvas.addEventListener('pointerup', e => {
    if (sx == null) return;
    const dx = e.clientX - sx;
    const dy = e.clientY - sy;
    sx = sy = null;
    if (Math.abs(dx) <= 40 || Math.abs(dx) <= Math.abs(dy)) return;
    swiped = true;
    if (photoMode()) { shown = (shown + (dx < 0 ? 1 : photos.length - 1)) % photos.length; refresh(); return; } // swipe through the photos
    setView((view + (dx < 0 ? 1 : 3)) % 4);
  });
  // a tap on the bow opens it full size (the buttons on the picture do their own thing)
  canvas.addEventListener('click', e => { if (!swiped && !e.target.closest('button, a, [data-gsb-locator]')) openFull(); });
  let lastWidth = width();
  window.addEventListener('resize', () => { if (width() !== lastWidth) { lastWidth = width(); draw(); } });

  $('[data-gsb-reset]').addEventListener('click', () => {
    const snap = snapshot();
    // clear only the real picks (form.reset() would also clear the Solid/Pattern and Color 1/2 switches)
    slots.forEach(s => { const r = $$('input[name$="--' + s.dataset.index + '"]', s); r.forEach(i => { i.checked = !i.value; }); delete s.dataset.fin; });
    form.querySelector('input[name="model"]').checked = true;
    Object.values(groups).forEach(g => { g.querySelectorAll('input').forEach(i => { i.checked = false; }); setErr(g, ''); });
    slots.forEach(p => setErr(p, ''));
    msg.textContent = '';
    if (customBox) { // custom lengths off, boxes and tick cleared
      customBox.querySelector('[data-gs-custom-on]').checked = false;
      customBox.querySelector('[data-gs-custom-body]').hidden = true;
      $$('[data-gs-custom-body] input', customBox).forEach(i => { if (i.type === 'checkbox') i.checked = false; else i.value = ''; i.setCustomValidity(''); i.disabled = true; });
      customErr('');
    }
    if (matchPane) { // back to "No", photos and description cleared (Undo brings them back)
      setMatch(false);
      photos.length = 0;
      notesEl.value = '';
      shown = 0;
      matchNote = '';
      setErr(photosBox, '');
      renderThumbs();
    }
    applyModel();
    setView(0, true);
    visited.clear();
    $('[data-gsb-linknote]').hidden = true;
    refresh();
    syncAllFinish();
    allTiles();
    go('model');
    showToast('Build cleared.', snap);
  });

  $('[data-gsb-share]').addEventListener('click', async () => {
    const link = location.origin + buildPath();
    if (navigator.share) {
      try { await navigator.share({ title: 'My ' + model().name + ' build', url: link }); return; }
      catch (err) { if (err && err.name === 'AbortError') return; }
    }
    try {
      await navigator.clipboard.writeText(link);
      shareMsg.textContent = 'Link copied. Send it to a friend, or to us if you have questions about your build.';
    } catch (err) {
      shareMsg.textContent = 'Copy this link: ' + link;
    }
  });
  if (!navigator.share) $('[data-gsb-share]').textContent = 'Copy link to this build';

  /* ---------- add to cart ---------- */
  const resetAdd = () => { adding = false; renderNav(); };
  window.addEventListener('pageshow', e => { if (e.persisted) resetAdd(); });
  form.addEventListener('submit', async e => {
    e.preventDefault();
    if (current !== 'review' || adding) return;
    msg.textContent = '';
    const m = model();
    const v = priceVariant(m);
    if (!v) return;
    if (photoJob) await photoJob;
    const missing = missingIn('review');
    if (missing.length) {
      missing.forEach(x => setErr(x.el, x.text));
      msg.textContent = 'Please pick: ' + missing.map(x => x.name).join(', ') + '.';
      renderReview();
      reveal(missing[0].el);
      return;
    }
    // custom lengths: at least one length and the "can't be returned" tick (not used while matching photos)
    if (customOn() && !matching()) {
      syncCustom();
      let bad = $$('input', customBox).find(i => !i.disabled && !i.checkValidity());
      let text = bad && (bad.type === 'checkbox' ? 'Please tick the box to say you understand custom lengths can\'t be returned.' : bad.validationMessage);
      if (!bad && !customLengths().length) { // backstop: the option is on but no length typed for this model
        bad = $$('[data-gsb-len]', customBox).find(i => !i.disabled);
        text = 'Type at least one length, or untick "I want custom string and cable lengths".';
      }
      if (bad) {
        msg.textContent = text;
        customErr(text); // shown next to the boxes too, since the step with the boxes is where we take them
        reveal(bad);
        return;
      }
    }
    let request;
    if (matching()) request = { headers: { Accept: 'application/json' }, body: matchForm(m, v) };
    else {
      const props = {};
      activeGroups().forEach(g => { props[groups[g].dataset.label] = groupValue(g) + (groups[g].dataset.unit || ''); });
      const codes = {};
      partPicks().forEach(p => p.rows.forEach(r => {
        props[r.label + ' color'] = rowText(r);
        codes[r.code] = r.inp.value || r.inp.dataset.black || 'black';
      }));
      Object.entries(blackParts()).forEach(([k, names]) => { props[k] = names.join(', '); });
      const custom = customLengths();
      custom.forEach(([k, v]) => { props['Custom ' + k.toLowerCase() + ' length'] = v; });
      if (custom.length) props['Custom lengths'] = customBox.querySelector('[data-gs-custom-ack]').value;
      // finish of each part that has one ("riser": "Anodize"); a pattern only where it's made a set way ("plimbs": "Film dip")
      const finish = {};
      visible().filter(s => s.dataset.index === '1').forEach(s => {
        const pat = picked(s)?.hasAttribute('data-pattern');
        if (pat && patFin(s)) finish[s.dataset.key] = patFin(s);
        else if (!pat && isAlu(s) && !isRaw(picked(s))) finish[s.dataset.key] = finOf(s); // Raw: no finish
      });
      const link = buildPath('', true); // with the custom lengths, so the cart's link back to the bow brings them too
      props._builder = JSON.stringify({ model: m.handle, hand: groupValue('hand'), draw: groupValue('draw'), weight: groupValue('weight'), colors: codes,
        finish, black: partPicks().filter(p => !p.rows.length && !p.required).map(p => p.key), link,
        ...(custom.length ? { custom: Object.fromEntries(custom) } : {}) });
      props._link = link;
      request = {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties: props }] }),
      };
    }
    adding = true;
    renderNav();
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', ...request });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(res.status < 500 && body.description ? String(body.description) : '');
      resetAdd();
      location.href = (window.Shopify?.routes?.root || '/') + 'cart';
    } catch (err) {
      const why = err.message ? ' ' + err.message.replace(/\.?$/, '.') : ' Please try again.';
      msg.textContent = 'We couldn\'t add your bow to the cart.' + why + ' You can also call or text us at ' + phone + ' to order.';
      msg.scrollIntoView({ behavior: smooth(), block: 'nearest' });
      resetAdd();
    }
  });

  /* ---------- phones: pinned picture height, auto-unpin, bar off screen ---------- */
  const setStage = () => {
    const hdr = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    // short screens (a phone on its side) never pin it either (gs-builder.css, max-height: 560px)
    left.classList.toggle('is-unpinned', phoneUI() && (hdr + left.offsetHeight > window.innerHeight * 0.55 || window.matchMedia('(max-height: 560px)').matches));
    // what the pinned picture covers under the header, so scroll targets land below it; nothing when it scrolls away
    // with the page (it used to count anyway, and on a phone held sideways each pick landed below the bottom of the screen)
    document.documentElement.style.setProperty('--gsb-stage-h', (pinnedPhone() ? Math.round(left.getBoundingClientRect().height) : 0) + 'px');
  };
  setStage();
  if ('ResizeObserver' in window) new ResizeObserver(setStage).observe(left);
  window.addEventListener('resize', setStage);
  const bar = $('[data-gsb-bar]');
  const setBar = () => document.documentElement.style.setProperty('--gsb-bar-h', Math.round(bar.getBoundingClientRect().height) + 'px');
  setBar();
  if ('ResizeObserver' in window) new ResizeObserver(setBar).observe(bar);
  // "All N" tiles: drop the fade once the rail is scrolled to its end
  form.addEventListener('scroll', e => {
    const box = e.target;
    if (box.classList && box.classList.contains('is-rail')) box.classList.toggle('is-end', box.scrollLeft + box.clientWidth >= box.scrollWidth - 4);
  }, true);
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([en]) => { bar.classList.toggle('is-hidden', !en.isIntersecting); }).observe($('[data-gsb-grid]'));
  }
})();
